import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './AttendanceReport.css';
import config from '../config';
import { Box, Typography, Tabs, Tab, Button } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DateRangeIcon from '@mui/icons-material/DateRange';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const AttendanceReport = () => {
  const [activeTab, setActiveTab] = useState(0);
  // Get yesterday's date for default
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };
  const [selectedDate, setSelectedDate] = useState(getYesterdayDate());
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedName, setSelectedName] = useState('all');
  const [selectedDesignation, setSelectedDesignation] = useState('all');
  const [selectedMachineType, setSelectedMachineType] = useState('all');
  const [selectedPresent, setSelectedPresent] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlyUserData, setMonthlyUserData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [yearlyUserData, setYearlyUserData] = useState([]);

  // New tab state: attendance grid (P/A per user per date)
  const [attendanceGridMonth, setAttendanceGridMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [attendanceGridMachineType, setAttendanceGridMachineType] = useState('all');
  const [attendanceGridDesignation, setAttendanceGridDesignation] = useState('all');
  const [attendanceGridSearch, setAttendanceGridSearch] = useState(''); // User search term
  const [attendanceGridData, setAttendanceGridData] = useState([]);
  const [allAttendanceGridUsers, setAllAttendanceGridUsers] = useState([]);
  const [attendanceGridLoading, setAttendanceGridLoading] = useState(false);
  const [attendanceGridError, setAttendanceGridError] = useState(null);

  // By Operator tab state
  const [byOperatorMonth, setByOperatorMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [byOperatorMachineType, setByOperatorMachineType] = useState('all');
  const [byOperatorDesignation, setByOperatorDesignation] = useState('all');
  const [byOperatorSearch, setByOperatorSearch] = useState(''); // User search term
  const [byOperatorData, setByOperatorData] = useState([]);
  const [allByOperatorUsers, setAllByOperatorUsers] = useState([]);
  const [byDateData, setByDateData] = useState([]); // Summary data by date
  const [byOperatorApiData, setByOperatorApiData] = useState([]); // Operator data from API
  const [byOperatorLoading, setByOperatorLoading] = useState(false);
  const [byOperatorError, setByOperatorError] = useState(null);
  const [isApiCallInProgress, setIsApiCallInProgress] = useState(false); // Track API call status
  const [abortController, setAbortController] = useState(null); // Track abort controller for request cancellation

  // Refs for top scrollbar synchronization
  const tableWrapperRef = React.useRef(null);
  const topScrollbarRef = React.useRef(null);
  const tableRef = React.useRef(null);
  const [tableWidth, setTableWidth] = useState(0);

  // Sync top scrollbar with table width
  useEffect(() => {
    if (activeTab === 6 && tableRef.current) {
      const updateWidth = () => {
        if (tableRef.current) {
          setTableWidth(tableRef.current.offsetWidth);
        }
      };

      updateWidth();
      window.addEventListener('resize', updateWidth);
      const timer = setTimeout(updateWidth, 500);
      return () => {
        window.removeEventListener('resize', updateWidth);
        clearTimeout(timer);
      };
    }
  }, [activeTab, byOperatorData, byOperatorMonth]);

  const handleTopScroll = (e) => {
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const handleTableScroll = (e) => {
    if (topScrollbarRef.current) {
      topScrollbarRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  useEffect(() => {
    fetchAttendanceData();
  }, [selectedDate, selectedMonth, selectedYear, activeTab]);

  // Load sample data for attendance grid when tab is active
  useEffect(() => {
    if (activeTab === 5) {
      loadAttendanceGridData();
    }
  }, [activeTab, attendanceGridMonth, attendanceGridMachineType, attendanceGridDesignation]);

  // Apply client-side filtering (including search) when filters change
  useEffect(() => {
    if (activeTab === 5 && allAttendanceGridUsers.length > 0) {
      applyAttendanceGridFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, attendanceGridMachineType, attendanceGridDesignation, attendanceGridSearch, allAttendanceGridUsers.length]);

  // Load By Operator data when tab is active or month/filters change
  useEffect(() => {
    let currentController = abortController;

    if (activeTab === 6) {
      console.log('🔄 useEffect triggered - Loading By Operator data', {
        month: byOperatorMonth,
        machineType: byOperatorMachineType,
        designation: byOperatorDesignation
      });

      // Clear ALL previous data and errors immediately when month/filters change
      // This ensures clean state when switching between months
      setByOperatorData([]);
      setByDateData([]);
      setByOperatorApiData([]);
      setAllByOperatorUsers([]);
      setByOperatorError(null); // Always clear errors when reloading

      // Trigger API call
      loadByOperatorDataFromAPI().catch(err => {
        // Ignore cancellation errors
        if (err.name === 'AbortError' || err.message === 'Request was cancelled') {
          console.log('ℹ️ Request was cancelled (likely due to filter change)');
          return;
        }
        console.error('❌ Unhandled error in loadByOperatorDataFromAPI:', err);
        // Ensure loading state is reset even if there's an unhandled error
        setByOperatorLoading(false);
        setIsApiCallInProgress(false);
      });
    } else {
      // Cancel any pending request when tab changes away from By Operator
      if (currentController) {
        console.log('🛑 Cancelling API request - tab changed away from By Operator');
        currentController.abort();
        setAbortController(null);
        setIsApiCallInProgress(false);
        setByOperatorLoading(false);
      }
    }

    // Cleanup: cancel request on unmount or when dependencies change
    return () => {
      if (currentController && !currentController.signal.aborted) {
        console.log('🛑 Cleanup: Cancelling pending API request');
        currentController.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, byOperatorMonth, byOperatorMachineType, byOperatorDesignation]);

  // Apply By Operator filters when filters change
  useEffect(() => {
    if (activeTab === 6 && byOperatorApiData.length > 0) {
      applyByOperatorFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, byOperatorMachineType, byOperatorDesignation, byOperatorSearch, byOperatorApiData.length]);

  // Static sample data for fallback
  const getStaticAttendanceGridData = () => {
    return [
      {
        name: 'Aashish Kumar',
        designation: 'Operator',
        machineType: 'Type A',
        attendance: {
          1: 'P', 2: 'A', 3: 'P', 4: 'P', 5: 'P', 6: 'A', 7: 'A', 8: 'A',
          9: 'P', 10: 'P', 11: 'P', 12: 'P', 13: 'P', 14: 'P', 15: 'P',
          16: 'A', 17: 'A', 18: 'A', 19: 'P', 20: 'P', 21: 'P', 22: 'P',
          23: 'P', 24: 'P', 25: 'P', 26: 'P', 27: 'P', 28: 'P', 29: 'P',
          30: 'P', 31: 'P'
        }
      },
      {
        name: 'John Doe',
        designation: 'Supervisor',
        machineType: 'Type B',
        attendance: {
          1: 'P', 2: 'P', 3: 'P', 4: 'A', 5: 'P', 6: 'P', 7: 'P', 8: 'P',
          9: 'P', 10: 'A', 11: 'P', 12: 'P', 13: 'P', 14: 'P', 15: 'P',
          16: 'P', 17: 'P', 18: 'P', 19: 'P', 20: 'A', 21: 'P', 22: 'P',
          23: 'P', 24: 'P', 25: 'P', 26: 'P', 27: 'P', 28: 'P', 29: 'P',
          30: 'P', 31: 'P'
        }
      },
      {
        name: 'Jane Smith',
        designation: 'Operator',
        machineType: 'Type A',
        attendance: {
          1: 'P', 2: 'P', 3: 'P', 4: 'P', 5: 'P', 6: 'P', 7: 'P', 8: 'P',
          9: 'P', 10: 'P', 11: 'A', 12: 'P', 13: 'P', 14: 'P', 15: 'P',
          16: 'P', 17: 'P', 18: 'P', 19: 'P', 20: 'P', 21: 'P', 22: 'P',
          23: 'P', 24: 'A', 25: 'P', 26: 'P', 27: 'P', 28: 'P', 29: 'P',
          30: 'P', 31: 'P'
        }
      },
      {
        name: 'Mike Johnson',
        designation: 'Technician',
        machineType: 'Type C',
        attendance: {
          1: 'P', 2: 'P', 3: 'A', 4: 'P', 5: 'P', 6: 'P', 7: 'P', 8: 'P',
          9: 'P', 10: 'P', 11: 'P', 12: 'P', 13: 'A', 14: 'P', 15: 'P',
          16: 'P', 17: 'P', 18: 'P', 19: 'P', 20: 'P', 21: 'P', 22: 'A',
          23: 'P', 24: 'P', 25: 'P', 26: 'P', 27: 'P', 28: 'P', 29: 'P',
          30: 'P', 31: 'A'
        }
      }
    ];
  };

  // Function to apply filters to attendance grid data (including search)
  const applyAttendanceGridFilters = () => {
    let filtered = [...allAttendanceGridUsers];

    // Filter by machine type
    if (attendanceGridMachineType !== 'all') {
      filtered = filtered.filter(user => user.machineType === attendanceGridMachineType);
    }

    // Filter by designation
    if (attendanceGridDesignation !== 'all') {
      filtered = filtered.filter(user => user.designation === attendanceGridDesignation);
    }

    // Filter by user search (case-insensitive)
    if (attendanceGridSearch.trim() !== '') {
      const searchTerm = attendanceGridSearch.trim().toLowerCase();
      filtered = filtered.filter(user =>
        user.name && user.name.toLowerCase().includes(searchTerm)
      );
    }

    setAttendanceGridData(filtered);
  };

  // Static sample data for By Operator tab
  const getStaticByOperatorData = () => {
    // Generate attendance for all 31 days with some variation
    const generateAttendance = (pattern) => {
      const attendance = {};
      for (let day = 1; day <= 31; day++) {
        if (pattern === 'mostlyPresent') {
          attendance[day] = day % 7 === 0 ? 'A' : 'P'; // Absent on Sundays
        } else if (pattern === 'mostlyAbsent') {
          attendance[day] = day % 3 === 0 ? 'P' : 'A'; // Present every 3rd day
        } else if (pattern === 'allPresent') {
          attendance[day] = 'P';
        } else if (pattern === 'alternating') {
          attendance[day] = day % 2 === 0 ? 'P' : 'A';
        } else {
          attendance[day] = day % 5 === 0 ? 'A' : 'P'; // Absent every 5th day
        }
      }
      return attendance;
    };

    return [
      {
        siNo: 1,
        name: 'Abbas',
        designation: 'Operator',
        userId: 'USR001',
        machineType: 'Type A',
        attendance: generateAttendance('mostlyPresent'),
        dateOfJoining: '2023-01-15',
        workTimeinMins: 14400,
        totalOTMins: 120,
        OTMins1_5: 180,
        OTPercentage: '12.5%',
        perHr: 500,
        otPay: 1000
      },
      {
        siNo: 2,
        name: 'Raghu',
        designation: 'Operator',
        userId: 'USR002',
        machineType: 'Type B',
        attendance: generateAttendance('mostlyAbsent'),
        dateOfJoining: '2023-02-20',
        workTimeinMins: 9600,
        totalOTMins: 90,
        OTMins1_5: 135,
        OTPercentage: '9.4%',
        perHr: 450,
        otPay: 675
      },
      {
        siNo: 3,
        name: 'Balvinder',
        designation: 'Supervisor',
        userId: 'USR003',
        machineType: 'Type A',
        attendance: generateAttendance('allPresent'),
        dateOfJoining: '2022-11-10',
        workTimeinMins: 17280,
        totalOTMins: 180,
        OTMins1_5: 270,
        OTPercentage: '15.6%',
        perHr: 600,
        otPay: 1800
      },
      {
        siNo: 4,
        name: 'Shyam',
        designation: 'Operator',
        userId: 'USR004',
        machineType: 'Type C',
        attendance: generateAttendance('mostlyPresent'),
        dateOfJoining: '2023-03-05',
        workTimeinMins: 12000,
        totalOTMins: 60,
        OTMins1_5: 90,
        OTPercentage: '5.0%',
        perHr: 400,
        otPay: 400
      },
      {
        siNo: 5,
        name: 'Rahim',
        designation: 'Technician',
        userId: 'USR005',
        machineType: 'Type B',
        attendance: generateAttendance('alternating'),
        dateOfJoining: '2023-04-12',
        workTimeinMins: 13200,
        totalOTMins: 150,
        OTMins1_5: 225,
        OTPercentage: '17.0%',
        perHr: 550,
        otPay: 1375
      }
    ];
  };

  // Apply filters for By Operator tab
  const applyByOperatorFilters = () => {
    let filtered = [...byOperatorApiData];

    // Filter by machine type
    if (byOperatorMachineType !== 'all') {
      filtered = filtered.filter(user => user.machineType === byOperatorMachineType);
    }

    // Filter by designation
    if (byOperatorDesignation !== 'all') {
      filtered = filtered.filter(user => user.designation === byOperatorDesignation);
    }

    // Filter by user search (case-insensitive)
    if (byOperatorSearch.trim() !== '') {
      const searchTerm = byOperatorSearch.trim().toLowerCase();
      filtered = filtered.filter(user =>
        (user.name && user.name.toLowerCase().includes(searchTerm)) ||
        (user.employeeName && user.employeeName.toLowerCase().includes(searchTerm)) ||
        (user.userName && user.userName.toLowerCase().includes(searchTerm)) ||
        (user.employeeId && user.employeeId.toLowerCase().includes(searchTerm))
      );
    }

    setByOperatorData(filtered);
  };

  // Helper function to retry API calls with exponential backoff and cancellation support
  const retryApiCall = async (apiCall, maxRetries = 2, initialDelay = 1000, abortSignal = null) => {
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      // Check if cancelled before each attempt
      if (abortSignal && abortSignal.aborted) {
        throw new Error('Request was cancelled');
      }

      try {
        const response = await apiCall();
        if (attempt > 1) {
          console.log(`✅ API call succeeded on attempt ${attempt}`);
        }
        return response;
      } catch (error) {
        // Don't retry if request was cancelled
        if (error.name === 'AbortError' || error.message === 'Request was cancelled' || (abortSignal && abortSignal.aborted)) {
          throw error;
        }

        lastError = error;

        // Don't retry on 4xx errors (client errors)
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          console.log(`❌ Client error (${error.response.status}), not retrying`);
          throw error;
        }

        // Don't retry on last attempt
        if (attempt === maxRetries) {
          console.log(`❌ All ${maxRetries} attempts failed`);
          break;
        }

        // Check if cancelled before waiting
        if (abortSignal && abortSignal.aborted) {
          throw new Error('Request was cancelled');
        }

        // Calculate delay with exponential backoff
        const delay = initialDelay * Math.pow(2, attempt - 1);
        console.log(`⚠️ Attempt ${attempt} failed, retrying in ${delay}ms... (${error.message})`);

        // Wait before retrying (with cancellation check)
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(resolve, delay);
          if (abortSignal) {
            abortSignal.addEventListener('abort', () => {
              clearTimeout(timeout);
              reject(new Error('Request was cancelled'));
            });
          }
        });
      }
    }

    throw lastError;
  };

  // Load By Operator data from API
  const loadByOperatorDataFromAPI = async () => {
    // Cancel any pending request
    if (abortController) {
      console.log('🛑 Cancelling previous API request...');
      abortController.abort();
      setAbortController(null);
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    setAbortController(controller);

    // Generate unique request ID for tracking
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    console.log(`🚀 [${requestId}] Starting API call for By Operator data for month: ${byOperatorMonth}...`);
    setIsApiCallInProgress(true);
    setByOperatorLoading(true);
    setByOperatorError(null); // Always clear errors at start of new API call

    // Clear previous data to ensure clean state
    setByOperatorData([]);
    setByDateData([]);
    setByOperatorApiData([]);
    setAllByOperatorUsers([]);

    // Initialize timeout watchdog
    let timeoutWatchdog = null;

    try {
      // Validate month parameter format (YYYY-MM)
      const monthRegex = /^\d{4}-\d{2}$/;
      if (!monthRegex.test(byOperatorMonth)) {
        throw new Error(`Invalid month format: ${byOperatorMonth}. Expected format: YYYY-MM`);
      }

      // Validate month values (year should be reasonable, month 01-12)
      const [year, month] = byOperatorMonth.split('-').map(Number);
      if (isNaN(year) || isNaN(month) || month < 1 || month > 12 || year < 2000 || year > 2100) {
        throw new Error(`Invalid month values: year=${year}, month=${month}. Month must be 01-12, year must be 2000-2100`);
      }

      // Prepare request payload
      const requestPayload = {
        month: byOperatorMonth, // Format: YYYY-MM
        machineType: byOperatorMachineType === 'all' ? null : byOperatorMachineType,
        designation: byOperatorDesignation === 'all' ? null : byOperatorDesignation
      };

      const startTime = performance.now();

      console.log(`📤 [${requestId}] Sending API request:`, {
        url: 'https://apir4prod.bsynapse.com/ifeccustomguard/getattendancegridv1',
        payload: requestPayload,
        month: byOperatorMonth,
        monthValid: monthRegex.test(byOperatorMonth),
        timestamp: new Date().toISOString()
      });

      // Check online status
      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your network.');
      }

      // Add timeout watchdog to detect stuck requests
      timeoutWatchdog = setTimeout(() => {
        if (!controller.signal.aborted) {
          console.warn(`⏰ [${requestId}] Request timeout watchdog triggered after 35 seconds`);
          console.warn(`⏰ [${requestId}] This request may be stuck. Consider cancelling.`);
          console.warn(`⏰ [${requestId}] Network tab should show if request is pending or failed`);
        }
      }, 35000); // 35 seconds (5 seconds after timeout)

      // Call API with retry logic, timeout, and cancellation support
      const response = await retryApiCall(async () => {
        // Check if request was cancelled
        if (controller.signal.aborted) {
          throw new Error('Request was cancelled');
        }

        const requestStartTime = performance.now();
        console.log(`🔄 [${requestId}] Attempting API call...`);

        try {
          console.log(`📤 [${requestId}] Sending request payload:`, JSON.stringify(requestPayload));

          const axiosResponse = await axios.post(
            'https://apir4prod.bsynapse.com/ifeccustomguard/getattendancegridv1',
            requestPayload,
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              timeout: 30000, // 30 second timeout
              signal: controller.signal, // Add cancellation support
              validateStatus: function (status) {
                // Don't throw error for 200-299 status codes
                return status >= 200 && status < 300;
              }
            }
          );

          const requestTime = performance.now() - requestStartTime;
          console.log(`✅ [${requestId}] API call completed successfully in ${(requestTime / 1000).toFixed(2)}s`);
          console.log(`📥 [${requestId}] Response status:`, axiosResponse.status);

          return axiosResponse;
        } catch (axiosError) {
          const requestTime = performance.now() - requestStartTime;
          console.error(`❌ [${requestId}] API call failed after ${(requestTime / 1000).toFixed(2)}s:`, {
            name: axiosError.name,
            message: axiosError.message,
            code: axiosError.code,
            response: axiosError.response ? {
              status: axiosError.response.status,
              statusText: axiosError.response.statusText,
              data: axiosError.response.data
            } : null,
            request: axiosError.request ? 'Request made but no response' : null
          });
          throw axiosError;
        }
      }, 2, 1000, controller.signal); // 2 retries with 1 second initial delay, pass abort signal

      const apiTime = performance.now() - startTime;
      console.log(`⏱️ [${requestId}] Total API call took ${(apiTime / 1000).toFixed(2)}s`);
      console.log(`📊 [${requestId}] By Operator API Response received`);

      // Clear timeout watchdog on success
      if (timeoutWatchdog) {
        clearTimeout(timeoutWatchdog);
        timeoutWatchdog = null;
      }

      // Log API response structure (simplified to avoid hanging on large responses)
      console.log('📋 API RESPONSE SUMMARY:', {
        status: response.data?.status,
        hasData: !!response.data?.data,
        dataLength: response.data?.data?.length || 0,
        hasByDate: !!response.data?.byDate,
        byDateKeys: response.data?.byDate ? Object.keys(response.data.byDate).length : 0,
        responseKeys: Object.keys(response.data || {})
      });

      // Log first byDate entry structure (only if exists)
      if (response.data?.byDate) {
        const firstByDateKey = Object.keys(response.data.byDate)[0];
        if (firstByDateKey) {
          console.log('📋 First byDate entry:', {
            date: firstByDateKey,
            fields: Object.keys(response.data.byDate[firstByDateKey]),
            sample: response.data.byDate[firstByDateKey]
          });
        }
      }

      // Log first user structure (only if exists)
      if (response.data?.data && response.data.data.length > 0) {
        const firstUser = response.data.data[0];
        console.log('📋 First User Structure:', {
          topLevelKeys: Object.keys(firstUser),
          hasByOperator: !!firstUser.byOperator,
          byOperatorKeys: firstUser.byOperator ? Object.keys(firstUser.byOperator) : [],
          hasAttendance: !!firstUser.attendance,
          attendanceKeysCount: firstUser.attendance ? Object.keys(firstUser.attendance).length : 0
        });
      }

      // API structure: { status: "success", data: [...users], byDate: {...}, filters: {...} }
      // Check if response has data, even if status is not explicitly 'success'
      if (response.data) {
        // If status exists and is not success, log warning but continue if we have data
        if (response.data.status && response.data.status !== 'success') {
          console.warn('⚠️ API returned non-success status:', response.data.status);
          console.warn('⚠️ But continuing with data if available...');
        }

        // Check if we have the required data structure
        const hasData = response.data.data && Array.isArray(response.data.data);
        const hasByDate = response.data.byDate && typeof response.data.byDate === 'object';

        if (!hasData && !hasByDate) {
          console.error('❌ API response missing required data structure:', {
            hasData: hasData,
            hasByDate: hasByDate,
            responseKeys: Object.keys(response.data)
          });
          throw new Error('API response missing required data structure');
        }

        // Wrap data processing in try-catch to prevent hanging
        try {
          const processingStartTime = performance.now();

          // Extract byDate object (keyed by date strings like "2026-02-01")
          const byDateObj = response.data.byDate || {};
          const byDateKeys = Object.keys(byDateObj);
          console.log(`📅 Processing ${byDateKeys.length} byDate entries for month: ${byOperatorMonth}`);

          // Get the selected month's year and month for validation
          const [selectedYear, selectedMonth] = byOperatorMonth.split('-').map(Number);
          const daysInSelectedMonth = getDaysInMonth(byOperatorMonth);

          // Convert byDate object to array format for easier day matching
          // Extract day number from date key (e.g., "2026-02-01" -> day 1)
          const byDateArray = byDateKeys
            .map(dateKey => {
              // Parse date string (format: YYYY-MM-DD)
              let day = null;
              let dateYear = null;
              let dateMonth = null;

              // Try multiple date formats
              if (dateKey.includes('-')) {
                const dateParts = dateKey.split('-');
                if (dateParts.length >= 3) {
                  dateYear = parseInt(dateParts[0], 10);
                  dateMonth = parseInt(dateParts[1], 10);
                  day = parseInt(dateParts[2], 10);
                }
              } else if (dateKey.includes('/')) {
                // Handle DD/MM/YYYY or MM/DD/YYYY format
                const dateParts = dateKey.split('/');
                if (dateParts.length >= 3) {
                  // Try to determine format by checking if first part > 12
                  if (parseInt(dateParts[0], 10) > 12) {
                    // DD/MM/YYYY format
                    day = parseInt(dateParts[0], 10);
                    dateMonth = parseInt(dateParts[1], 10);
                    dateYear = parseInt(dateParts[2], 10);
                  } else {
                    // MM/DD/YYYY format
                    dateMonth = parseInt(dateParts[0], 10);
                    day = parseInt(dateParts[1], 10);
                    dateYear = parseInt(dateParts[2], 10);
                  }
                }
              } else {
                // Try parsing as ISO date string
                try {
                  const parsedDate = new Date(dateKey);
                  if (!isNaN(parsedDate.getTime())) {
                    dateYear = parsedDate.getFullYear();
                    dateMonth = parsedDate.getMonth() + 1;
                    day = parsedDate.getDate();
                  }
                } catch (e) {
                  console.warn(`⚠️ Could not parse date: ${dateKey}`);
                }
              }

              // Validate that the date belongs to the selected month
              const isValidDate = day !== null &&
                day >= 1 &&
                day <= daysInSelectedMonth &&
                dateYear === selectedYear &&
                dateMonth === selectedMonth;

              if (!isValidDate && day !== null) {
                console.warn(`⚠️ Date ${dateKey} (day ${day}) does not belong to selected month ${byOperatorMonth}`);
              }

              const dateObj = byDateObj[dateKey];

              // Log all available fields in dateObj for first entry to detect missing fields
              if (dateKey === byDateKeys[0] && dateObj) {
                console.log('📋 Available fields in byDate entry:', {
                  date: dateKey,
                  allFields: Object.keys(dateObj),
                  allValues: dateObj
                });
              }

              // Map with multiple field name variations (check all possible variations)
              const mappedDate = {
                day: isValidDate ? day : null,
                date: dateKey,
                year: dateYear,
                month: dateMonth,
                isValid: isValidDate,
                // Try all possible field name variations
                totalPresent: dateObj?.totalPresent ?? dateObj?.total_present ?? dateObj?.totalPresentCount ?? dateObj?.presentCount ?? dateObj?.present ?? 0,
                totalAbsent: dateObj?.totalAbsent ?? dateObj?.total_absent ?? dateObj?.totalAbsentCount ?? dateObj?.absentCount ?? dateObj?.absent ?? 0,
                percentageOfPresence: dateObj?.percentageOfPresence ?? dateObj?.percentage_of_presence ?? dateObj?.presencePercentage ?? dateObj?.presence_percentage ?? dateObj?.presencePercent ?? 0,
                percentageOfAbsence: dateObj?.percentageOfAbsence ?? dateObj?.percentage_of_absence ?? dateObj?.absencePercentage ?? dateObj?.absence_percentage ?? dateObj?.absencePercent ?? 0,
                OTMins: dateObj?.OTMins ?? dateObj?.ot_mins ?? dateObj?.otMinutes ?? dateObj?.ot_minutes ?? dateObj?.totalOTMins ?? dateObj?.total_ot_mins ?? dateObj?.overtimeMins ?? 0,
                OTPercentage: dateObj?.OTPercentage ?? dateObj?.ot_percentage ?? dateObj?.otPercentage ?? dateObj?.ot_percent ?? dateObj?.overtimePercentage ?? 0,
                workTimeinHrs: dateObj?.workTimeinHrs ?? dateObj?.workTimeInHrs ?? dateObj?.work_time_in_hrs ?? dateObj?.workTimeInHours ?? dateObj?.totalWorkHours ?? dateObj?.total_work_hours ?? dateObj?.workHours ?? dateObj?.work_hours ?? 0,
                OTHrs: dateObj?.OTHrs ?? dateObj?.OTHours ?? dateObj?.ot_hrs ?? dateObj?.otHours ?? dateObj?.totalOTHrs ?? dateObj?.total_ot_hrs ?? dateObj?.overtimeHrs ?? dateObj?.overtime_hrs ?? 0
              };

              return mappedDate;
            })
            .filter(item => item.isValid && item.day !== null); // Only keep valid dates for the selected month

          // Log validation results
          const validDates = byDateArray.length;
          const invalidDates = byDateKeys.length - validDates;
          if (invalidDates > 0) {
            console.warn(`⚠️ Filtered out ${invalidDates} invalid dates (not in selected month)`);
          }
          console.log(`✅ Valid dates for month ${byOperatorMonth}: ${validDates} out of ${byDateKeys.length}`);

          // Sort by day to ensure correct order
          byDateArray.sort((a, b) => a.day - b.day);

          // Log mapping comparison for first few dates
          if (byDateArray.length > 0) {
            console.log('🔍 MAPPING COMPARISON - byDate (first 3 entries):');
            byDateArray.slice(0, 3).forEach(mappedItem => {
              const originalDate = byDateObj[mappedItem.date];
              console.log(`  Day ${mappedItem.day} (${mappedItem.date}):`, {
                'Original API Data': originalDate,
                'Mapped Data': mappedItem,
                'Field Mapping': {
                  totalPresent: `${originalDate?.totalPresent ?? originalDate?.total_present ?? 'N/A'} → ${mappedItem.totalPresent}`,
                  totalAbsent: `${originalDate?.totalAbsent ?? originalDate?.total_absent ?? 'N/A'} → ${mappedItem.totalAbsent}`,
                  workTimeinHrs: `${originalDate?.workTimeinHrs ?? originalDate?.workTimeInHrs ?? originalDate?.work_time_in_hrs ?? 'N/A'} → ${mappedItem.workTimeinHrs}`,
                  OTHrs: `${originalDate?.OTHrs ?? originalDate?.OTHours ?? originalDate?.ot_hrs ?? 'N/A'} → ${mappedItem.OTHrs}`
                }
              });
            });
          }

          const byDateTime = performance.now() - processingStartTime;
          console.log(`⏱️ byDate processing took ${(byDateTime / 1000).toFixed(2)}s`);
          setByDateData(byDateArray);

          // Extract user data array (this is the byOperator data)
          const usersArray = response.data.data || [];
          console.log(`✅ Processing ${usersArray.length} users for month: ${byOperatorMonth}`);

          // Validate and log attendance data structure
          if (usersArray.length > 0) {
            const firstUser = usersArray[0];
            const attendanceKeys = firstUser.attendance ? Object.keys(firstUser.attendance) : [];
            const attendanceDayNumbers = attendanceKeys
              .map(key => {
                // Try to extract day number from key
                const num = parseInt(key, 10);
                return isNaN(num) ? null : num;
              })
              .filter(day => day !== null && day >= 1 && day <= daysInSelectedMonth);

            console.log(`📋 First user sample:`, {
              name: firstUser.name,
              hasByOperator: !!firstUser.byOperator,
              attendanceKeysCount: attendanceKeys.length,
              validDayNumbers: attendanceDayNumbers.length,
              dayRange: attendanceDayNumbers.length > 0
                ? `${Math.min(...attendanceDayNumbers)}-${Math.max(...attendanceDayNumbers)}`
                : 'none',
              expectedDays: daysInSelectedMonth
            });

            // Warn if attendance data doesn't match expected days
            if (attendanceDayNumbers.length > 0) {
              const maxDay = Math.max(...attendanceDayNumbers);
              if (maxDay > daysInSelectedMonth) {
                console.warn(`⚠️ User attendance data contains days beyond month limit (max: ${maxDay}, expected: ${daysInSelectedMonth})`);
              }
            }
          }

          // Map users to include byOperator data at top level for easier access
          const mappingStartTime = performance.now();
          const mappedUsers = usersArray.map((user, index) => {
            const byOp = user.byOperator || {};

            // Get attendance data and validate day numbers
            const rawAttendance = user.attendance ?? user.attendanceData ?? byOp.attendance ?? {};
            const validatedAttendance = {};

            // Filter and validate attendance data to only include valid days for the selected month
            Object.keys(rawAttendance).forEach(key => {
              const dayNum = parseInt(key, 10);
              // Accept numeric keys that are valid days (1 to daysInSelectedMonth)
              // Also accept string keys like "day1", "Day1", "d1" etc.
              if (!isNaN(dayNum) && dayNum >= 1 && dayNum <= daysInSelectedMonth) {
                validatedAttendance[dayNum] = rawAttendance[key];
              } else if (typeof key === 'string') {
                // Try to extract day number from string keys like "day1", "Day1", "d1"
                const match = key.match(/(?:day|d|Day|D)?(\d+)/i);
                if (match) {
                  const extractedDay = parseInt(match[1], 10);
                  if (extractedDay >= 1 && extractedDay <= daysInSelectedMonth) {
                    validatedAttendance[extractedDay] = rawAttendance[key];
                  }
                }
              }
            });

            // Log warning for first user if there are invalid attendance keys
            if (index === 0 && Object.keys(rawAttendance).length !== Object.keys(validatedAttendance).length) {
              const invalidKeys = Object.keys(rawAttendance).filter(key => {
                const dayNum = parseInt(key, 10);
                return isNaN(dayNum) || dayNum < 1 || dayNum > daysInSelectedMonth;
              });
              if (invalidKeys.length > 0) {
                console.warn(`⚠️ First user has ${invalidKeys.length} invalid attendance keys (filtered out):`, invalidKeys.slice(0, 5));
              }
            }

            // Log all available fields for first user to detect missing fields
            if (index === 0) {
              console.log('📋 Available fields in user object:', {
                topLevelFields: Object.keys(user),
                byOperatorFields: byOp ? Object.keys(byOp) : [],
                byOperatorValues: byOp
              });
            }

            // Map with comprehensive field access (check all possible variations)
            return {
              ...user,
              // Basic user info - check all possible field name variations
              name: user.name ?? user.employeeName ?? user.employee_name ?? user.fullName ?? user.full_name ?? user.empName ?? '-',
              designation: user.designation ?? user.designationName ?? user.designation_name ?? user.role ?? user.roleName ?? '-',
              userName: user.userName ?? user.username ?? user.user_name ?? user.employeeId ?? user.employee_id ?? user.userId ?? user.user_id ?? '-',
              employeeId: user.employeeId ?? user.employee_id ?? user.userName ?? user.username ?? '-',
              // Flatten byOperator data to top level - check all possible field name variations
              totalPresent: byOp.totalPresent ?? byOp.total_present ?? byOp.totalPresentCount ?? byOp.presentCount ?? user.totalPresent ?? user.total_present ?? 0,
              totalAbsent: byOp.totalAbsent ?? byOp.total_absent ?? byOp.totalAbsentCount ?? byOp.absentCount ?? user.totalAbsent ?? user.total_absent ?? 0,
              percentageOfPresence: byOp.percentageOfPresence ?? byOp.percentage_of_presence ?? byOp.presencePercentage ?? byOp.presence_percentage ?? user.percentageOfPresence ?? user.percentage_of_presence ?? '0%',
              percentageOfAbsence: byOp.percentageOfAbsence ?? byOp.percentage_of_absence ?? byOp.absencePercentage ?? byOp.absence_percentage ?? user.percentageOfAbsence ?? user.percentage_of_absence ?? '0%',
              workTimeinMins: byOp.workTimeinMins ?? byOp.workTimeInMins ?? byOp.work_time_in_mins ?? byOp.workTimeInMinutes ?? byOp.totalWorkMinutes ?? byOp.total_work_minutes ?? user.workTimeinMins ?? user.workTimeInMins ?? user.work_time_in_mins ?? 0,
              workTimeinHrs: byOp.workTimeinHrs ?? byOp.workTimeInHrs ?? byOp.work_time_in_hrs ?? byOp.workTimeInHours ?? byOp.totalWorkHours ?? byOp.total_work_hours ?? user.workTimeinHrs ?? user.workTimeInHrs ?? user.work_time_in_hrs ?? 0,
              OTMins: byOp.OTMins ?? byOp.ot_mins ?? byOp.otMinutes ?? byOp.ot_minutes ?? byOp.totalOTMins ?? byOp.total_ot_mins ?? user.OTMins ?? user.ot_mins ?? 0,
              OTHrs: byOp.OTHrs ?? byOp.OTHours ?? byOp.ot_hrs ?? byOp.otHours ?? byOp.totalOTHrs ?? byOp.total_ot_hrs ?? user.OTHrs ?? user.OTHours ?? user.ot_hrs ?? 0,
              OTMins1_5: byOp.OTMins1_5 ?? byOp['OTMins1.5'] ?? byOp.OTMins15 ?? byOp.otMins1_5 ?? byOp.ot_mins_1_5 ?? user.OTMins1_5 ?? user['OTMins1.5'] ?? 0,
              OTPercentage: byOp.OTPercentage ?? byOp.otPercentage ?? byOp.OT_Percentage ?? byOp.ot_percentage ?? byOp.otPercent ?? byOp.ot_percent ?? user.OTPercentage ?? user.otPercentage ?? '0%',
              perHourPay: byOp.perHourPay ?? byOp.per_hour_pay ?? byOp.hourlyPay ?? byOp.hourly_pay ?? user.perHourPay ?? user.per_hour_pay ?? '0',
              OTPay: byOp.OTPay ?? byOp.ot_pay ?? byOp.otPay ?? byOp.overtimePay ?? byOp.overtime_pay ?? user.OTPay ?? user.ot_pay ?? 0,
              dateOfJoining: byOp.dateOfJoining ?? byOp.date_of_joining ?? byOp.joiningDate ?? byOp.joining_date ?? user.dateOfJoining ?? user.date_of_joining ?? '',
              // Use validated attendance data (only valid days for the selected month)
              attendance: validatedAttendance
            };
          });

          const mappingTime = performance.now() - mappingStartTime;
          console.log(`⏱️ User mapping took ${(mappingTime / 1000).toFixed(2)}s`);
          console.log(`✅ Total processing time: ${((performance.now() - processingStartTime) / 1000).toFixed(2)}s`);

          // Log mapping comparison for first user to verify correctness
          if (mappedUsers.length > 0 && usersArray.length > 0) {
            const firstOriginal = usersArray[0];
            const firstMapped = mappedUsers[0];
            console.log('🔍 MAPPING COMPARISON - First User:');
            console.log('  Original API Data:', {
              name: firstOriginal.name,
              designation: firstOriginal.designation,
              userName: firstOriginal.userName,
              byOperator: firstOriginal.byOperator,
              attendance: firstOriginal.attendance ? Object.keys(firstOriginal.attendance).slice(0, 5) : null
            });
            console.log('  Mapped Data:', {
              name: firstMapped.name,
              designation: firstMapped.designation,
              userName: firstMapped.userName,
              totalPresent: firstMapped.totalPresent,
              totalAbsent: firstMapped.totalAbsent,
              workTimeinHrs: firstMapped.workTimeinHrs,
              OTHrs: firstMapped.OTHrs,
              attendance: firstMapped.attendance ? Object.keys(firstMapped.attendance).slice(0, 5) : null
            });
            console.log('  Field Mapping Verification:', {
              totalPresent: `API: ${firstOriginal.byOperator?.totalPresent ?? firstOriginal.totalPresent ?? 'N/A'} → Mapped: ${firstMapped.totalPresent}`,
              totalAbsent: `API: ${firstOriginal.byOperator?.totalAbsent ?? firstOriginal.totalAbsent ?? 'N/A'} → Mapped: ${firstMapped.totalAbsent}`,
              workTimeinHrs: `API: ${firstOriginal.byOperator?.workTimeinHrs ?? firstOriginal.workTimeinHrs ?? 'N/A'} → Mapped: ${firstMapped.workTimeinHrs}`,
              OTHrs: `API: ${firstOriginal.byOperator?.OTHrs ?? firstOriginal.OTHrs ?? 'N/A'} → Mapped: ${firstMapped.OTHrs}`
            });
          }

          // Only set data if we have valid mapped data from API
          if (mappedUsers.length > 0 || byDateArray.length > 0) {
            setByOperatorApiData(mappedUsers);
            setAllByOperatorUsers(mappedUsers);
            console.log('✅ Data processing completed successfully');
            console.log(`✅ Loaded ${mappedUsers.length} users and ${byDateArray.length} date entries from API`);
          } else {
            console.warn('⚠️ API returned empty data - no users or dates found');
            setByOperatorApiData([]);
            setAllByOperatorUsers([]);
            setByDateData([]);
            setByOperatorError('API returned empty data. Please check the selected month has data.');
          }
        } catch (processingError) {
          console.error('❌ Error during data processing:', processingError);
          console.error('❌ Processing error stack:', processingError.stack);
          throw new Error(`Data processing failed: ${processingError.message}`);
        }
      } else {
        console.error('❌ Unexpected API response format:', response.data);
        throw new Error('Unexpected API response format');
      }
    } catch (error) {
      // Clear timeout watchdog on error
      if (timeoutWatchdog) {
        clearTimeout(timeoutWatchdog);
        timeoutWatchdog = null;
      }

      // Don't show error if request was cancelled (user changed month)
      if (error.name === 'AbortError' || error.message === 'Request was cancelled' || controller.signal.aborted) {
        console.log(`ℹ️ [${requestId}] API request was cancelled (likely due to month filter change)`);
        return; // Exit silently if cancelled
      }

      console.error(`❌ [${requestId}] Error in loadByOperatorDataFromAPI:`, error);
      console.error('❌ Error type:', error.constructor.name);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);

      // Extract detailed error information
      let errorMessage = 'Failed to load data from API. Please try again.';
      let errorDetails = '';

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const statusText = error.response.statusText;
        const responseData = error.response.data;

        errorDetails = `Server Error: ${status} ${statusText}`;
        if (responseData) {
          if (typeof responseData === 'string') {
            errorDetails += ` - ${responseData}`;
          } else if (responseData.message) {
            errorDetails += ` - ${responseData.message}`;
          } else if (responseData.error) {
            errorDetails += ` - ${responseData.error}`;
          }
        }

        console.error('📋 Error Response:', {
          status,
          statusText,
          data: responseData,
          headers: error.response.headers
        });

        errorMessage = `API Error (${status}): ${statusText}. Please check the API and try again.`;
      } else if (error.request) {
        // Request was made but no response received
        errorDetails = 'No response from server. Check network connection.';
        console.error('📋 Request made but no response:', error.request);

        // Check if browser is online
        if (!navigator.onLine) {
          errorMessage = 'Network Error: No internet connection detected. Please check your network and try again.';
        } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
          errorMessage = 'Network Error: Unable to reach server. The server may be down or unreachable. Please try again in a moment.';
        } else if (error.code === 'ECONNABORTED') {
          errorMessage = 'Network Error: Request timed out. The server took too long to respond. Please try again.';
        } else {
          errorMessage = 'Network Error: No response from server. This may be a temporary issue. Please try again.';
        }

        // Provide troubleshooting tips
        console.error('💡 Troubleshooting tips:');
        console.error('   1. Check your internet connection');
        console.error('   2. Verify the server is accessible');
        console.error('   3. Try refreshing the page');
        console.error('   4. Check browser console for CORS errors');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        // Timeout error
        errorDetails = 'Request timed out after 60 seconds.';
        console.error('📋 Request timeout:', error);
        errorMessage = 'Request Timeout: Server took too long to respond. Please try again.';
      } else {
        // Something else happened
        errorDetails = error.message || 'Unknown error occurred.';
        console.error('📋 Other error:', error);
        errorMessage = `Error: ${error.message || 'Unknown error'}. Please try again.`;
      }

      console.error('📋 Full error details:', {
        message: error.message,
        code: error.code,
        details: errorDetails,
        stack: error.stack
      });

      setByOperatorError(errorMessage);

      // DO NOT use static data - only use API response
      // Clear data on error to show that API failed
      setByDateData([]);
      setByOperatorApiData([]);
      setAllByOperatorUsers([]);

      console.error('❌ API call failed - No data loaded. Please check the error above and try again.');
    } finally {
      // Only reset if this request wasn't cancelled
      if (!controller.signal.aborted) {
        setByOperatorLoading(false);
        setIsApiCallInProgress(false);
        setAbortController(null);
      }
    }
  };

  // NOTE: Removed static data fallback - all data must come from API
  // This function is kept for reference but should not be used

  // Function to load attendance grid data from API with static fallback
  const loadAttendanceGridData = async () => {
    setAttendanceGridLoading(true);
    setAttendanceGridError(null);

    try {
      // Prepare request payload
      const requestPayload = {
        month: attendanceGridMonth, // Format: YYYY-MM
        machineType: attendanceGridMachineType === 'all' ? null : attendanceGridMachineType,
        designation: attendanceGridDesignation === 'all' ? null : attendanceGridDesignation
      };

      // Call API
      const response = await axios.post(
        'https://apir4prod.bsynapse.com/ifeccustomguard/getattendancegrid',
        requestPayload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Check if response has data
      let apiUsers = null;

      // Case 1: backend returns array directly
      if (response.data && Array.isArray(response.data)) {
        apiUsers = response.data;
      }
      // Case 2: backend follows spec: { status: 'success', data: [...] }
      else if (
        response.data &&
        Array.isArray(response.data.data)
      ) {
        apiUsers = response.data.data;
      }

      if (apiUsers) {
        // Keep full list for filter dropdowns
        setAllAttendanceGridUsers(apiUsers);
        // Filters will be applied by useEffect when allAttendanceGridUsers changes
      } else {
        // If API response format is unexpected, use static data
        throw new Error('Unexpected API response format');
      }
    } catch (error) {
      console.error('Error fetching attendance grid data from API:', error);
      setAttendanceGridError('Failed to load data from API. Please try again.');

      // DO NOT use static data - only use API response
      setAllAttendanceGridUsers([]);
      setAttendanceGridData([]);
    } finally {
      setAttendanceGridLoading(false);
    }
  };

  // Helper to get all months (current year) for month dropdown
  const getAllMonths = () => {
    const months = [];
    const currentYear = new Date().getFullYear();
    const monthFormatter = new Intl.DateTimeFormat('en', { month: 'long' });

    for (let i = 0; i < 12; i++) {
      const date = new Date(currentYear, i, 1);
      const value = `${currentYear}-${String(i + 1).padStart(2, '0')}`; // YYYY-MM
      months.push({
        value,
        label: `${monthFormatter.format(date)} ${currentYear}`
      });
    }

    return months;
  };

  // Helper to get days in a given month (YYYY-MM)
  const getDaysInMonth = (yearMonth) => {
    // Parse year-month string (format: YYYY-MM)
    const parts = yearMonth.split('-');
    if (parts.length !== 2) {
      console.warn(`⚠️ Invalid yearMonth format: ${yearMonth}, defaulting to 31 days`);
      return 31;
    }

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);

    // Validate year and month
    if (isNaN(year) || isNaN(month) || month < 1 || month > 12) {
      console.warn(`⚠️ Invalid year or month: ${yearMonth}, defaulting to 31 days`);
      return 31;
    }

    // Use Date constructor to get days in month (handles leap years automatically)
    // new Date(year, month, 0) gives the last day of the previous month
    // So new Date(year, month + 1, 0) gives the last day of the current month
    const daysInMonth = new Date(year, month, 0).getDate();

    // Log for debugging (only for edge cases)
    if (month === 2) {
      console.log(`📅 ${yearMonth}: ${daysInMonth} days (${daysInMonth === 29 ? 'Leap year' : 'Non-leap year'})`);
    }

    return daysInMonth;
  };

  // Helper function to preserve time format (colon format "HH:MM" or numeric)
  const preserveTimeFormat = (timeValue) => {
    if (!timeValue && timeValue !== 0) return '00:00';

    // If it's already a string with colon format, return as is
    if (typeof timeValue === 'string' && timeValue.includes(':')) {
      return timeValue;
    }

    // If it's a number, convert to colon format
    if (typeof timeValue === 'number' || (typeof timeValue === 'string' && !isNaN(parseFloat(timeValue)))) {
      const hours = Math.floor(parseFloat(timeValue));
      const minutes = Math.round((parseFloat(timeValue) - hours) * 60);
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    return timeValue;
  };

  // Helper function to format hours with colon (HH:MM) for display
  const formatHoursWithColon = (hoursValue) => {
    if (hoursValue === null || hoursValue === undefined || hoursValue === '') {
      return '00:00';
    }

    // If it's already a string with colon format, return as is
    if (typeof hoursValue === 'string' && hoursValue.includes(':')) {
      return hoursValue;
    }

    // If it's a number, convert to colon format
    if (typeof hoursValue === 'number' || (typeof hoursValue === 'string' && !isNaN(parseFloat(hoursValue)))) {
      const hours = Math.floor(parseFloat(hoursValue));
      const minutes = Math.round((parseFloat(hoursValue) - hours) * 60);
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    return hoursValue;
  };

  // Helper function to convert workTime to hours (used in mapping and total calculations)
  const convertWorkTimeToHours = (workTime) => {
    if (workTime === null || workTime === undefined || workTime === '') {
      return 0;
    }
    if (typeof workTime === 'number') {
      return workTime;
    }
    if (typeof workTime === 'string' && workTime.includes(':')) {
      const parts = workTime.split(':');
      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      return hours + (minutes / 60);
    }
    const parsed = parseFloat(workTime);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Helper function to map daily API fields to UI fields
  const mapDailyData = (apiData) => {
    if (!Array.isArray(apiData)) return [];
    return apiData.map(item => {
      // Helper to preserve time format (HH:MM) or convert numeric to HH:MM
      const preserveTimeValue = (value) => {
        if (value === null || value === undefined || value === '') {
          return '00:00';
        }
        // If it's already a string with colon format, return as is
        if (typeof value === 'string' && value.includes(':')) {
          return value;
        }
        // If it's a number or numeric string, convert to colon format
        if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
          const numValue = parseFloat(value);
          const hours = Math.floor(numValue);
          const minutes = Math.round((numValue - hours) * 60);
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }
        return value;
      };

      // Get workTime value - preserve format if it's already in HH:MM
      const workTimeValue = item.workTime || item.work_time || item.regularHours || item.regular_hours || '00:00';
      // Get overTime value - preserve format if it's already in HH:MM
      const overTimeValue = item.overTime || item.over_time || item.otHours || item.ot_hours || item.overtime || '00:00';
      // Get machineTime value - preserve format if it's already in HH:MM
      const machineTimeValue = item.machineTime || item.machine_time || '00:00';

      return {
        userName: item.userName || item.username || item.user_name || item.employeeId || '-',
        employeeName: item.employeeName || item.employee_name || item.name || '-',
        employeeId: item.employeeId || item.employee_id || '-',
        machineType: item.machineType || item.machine_type || item.machineType || '-',
        designation: item.designation || item.Designation || item.DESIGNATION || '-',
        present: item.present === 'Yes' || item.present === 'yes' || item.present === 'YES' || item.present === 1 || item.present === '1' ? 1 : 0,
        absent: item.present === 'No' || item.present === 'no' || item.present === 'NO' || item.present === 0 || item.present === '0' ? 1 : 0,
        // Map workTime from API to Work Time (Hrs) - preserve HH:MM format
        workTime: preserveTimeValue(workTimeValue),
        // Map overTime from API to Over Time (Hrs) - preserve HH:MM format
        overTime: preserveTimeValue(overTimeValue),
        // Map overTime1.5 from API to Over Time 1.5 Times (Mins) - check for dot notation first
        overTime1_5: parseFloat(item['overTime1.5'] || item.overTime1_5 || item.overTime15 || item.over_time_1_5 || item.over_time_15 || item.overtime1_5 || item.overtime15 || 0),
        perHourRate: parseFloat(item.perHourRate || item.per_hour_rate || item.perHour || item.per_hour || 0),
        overTimePa: parseFloat(item.overTimePay || item.overTimePa || item.over_time_pay || item.over_time_pa || item.overtimePay || item.overtimePa || item.overtime_pay || item.overtime_pa || 0),
        // Map machineTime from API to Machine Time - preserve HH:MM format
        machineTime: preserveTimeValue(machineTimeValue),
        // For backward compatibility with existing UI
        name: item.employeeName || item.employee_name || item.userName || item.username || '-',
        regularHours: preserveTimeValue(workTimeValue),
        otHours: preserveTimeValue(overTimeValue),
        // Calculate total as sum of workTime and overTime in hours, then format as HH:MM
        total: (() => {
          const workHours = convertWorkTimeToHours(workTimeValue);
          const otHours = convertWorkTimeToHours(overTimeValue);
          const totalHours = workHours + otHours;
          const hours = Math.floor(totalHours);
          const minutes = Math.round((totalHours - hours) * 60);
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        })()
      };
    });
  };

  // Helper function to map monthly user API fields to UI fields
  // Maps: employeeName, employeeId, userName, totalPresent, totalAbsent, totalWorkTime, totalOverTime
  const mapMonthlyUserData = (apiData) => {
    console.log('=== mapMonthlyUserData START ===');
    console.log('Input apiData:', apiData);

    if (!Array.isArray(apiData) || apiData.length === 0) {
      console.log('mapMonthlyUserData: No data or not an array');
      return [];
    }

    console.log('Processing', apiData.length, 'items');

    // Directly map API response fields to UI fields
    const mappedData = apiData.map((item, index) => {
      // Map fields directly from API response
      // Preserve time format as string from API (e.g., "HH:MM")
      let totalWorkTime = item.totalWorkTime || item.total_work_time || '00:00';
      let totalOverTime = item.totalOverTime || item.total_over_time || '00:00';

      // If time values are numbers, convert to string format, otherwise use as-is
      if (typeof totalWorkTime === 'number') {
        const hours = Math.floor(totalWorkTime);
        const minutes = Math.round((totalWorkTime - hours) * 60);
        totalWorkTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      } else if (typeof totalWorkTime === 'string' && !totalWorkTime.includes(':')) {
        // If it's a string number, convert to colon format
        const numValue = parseFloat(totalWorkTime);
        if (!isNaN(numValue)) {
          const hours = Math.floor(numValue);
          const minutes = Math.round((numValue - hours) * 60);
          totalWorkTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        } else {
          totalWorkTime = '00:00';
        }
      } else if (!totalWorkTime || totalWorkTime === '') {
        totalWorkTime = '00:00';
      }

      if (typeof totalOverTime === 'number') {
        const hours = Math.floor(totalOverTime);
        const minutes = Math.round((totalOverTime - hours) * 60);
        totalOverTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      } else if (typeof totalOverTime === 'string' && !totalOverTime.includes(':')) {
        // If it's a string number, convert to colon format
        const numValue = parseFloat(totalOverTime);
        if (!isNaN(numValue)) {
          const hours = Math.floor(numValue);
          const minutes = Math.round((numValue - hours) * 60);
          totalOverTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        } else {
          totalOverTime = '00:00';
        }
      } else if (!totalOverTime || totalOverTime === '') {
        totalOverTime = '00:00';
      }

      // Convert totalAbsentDaysInMins from minutes to HH:MM format
      let totalAbsentHrsMins = '00:00';
      const absentMins = parseInt(item.totalAbsentDaysInMins || item.total_absent_days_in_mins || item.totalAbsentDaysInMinutes || item.total_absent_days_in_minutes || 0);
      if (absentMins > 0) {
        const hours = Math.floor(absentMins / 60);
        const minutes = absentMins % 60;
        totalAbsentHrsMins = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }

      // Convert adjustedOT to HH:MM format if needed
      let adjustedOT = item.adjustedOT || item.adjusted_ot || item.adjustedOt || '00:00';
      if (typeof adjustedOT === 'number') {
        const hours = Math.floor(adjustedOT);
        const minutes = Math.round((adjustedOT - hours) * 60);
        adjustedOT = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      } else if (typeof adjustedOT === 'string' && !adjustedOT.includes(':')) {
        const numValue = parseFloat(adjustedOT);
        if (!isNaN(numValue)) {
          const hours = Math.floor(numValue);
          const minutes = Math.round((numValue - hours) * 60);
          adjustedOT = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        } else {
          adjustedOT = '00:00';
        }
      } else if (!adjustedOT || adjustedOT === '') {
        adjustedOT = '00:00';
      }

      // Get perHourRate and OT_Pay from API
      const perHourRate = parseFloat(item.perHourRate || 0);
      const otPay = parseFloat(item.OT_Pay || 0);

      // Get totalOverMinutes from API (raw minutes value)
      const totalOverMinutes = parseInt(item.totalOverMinutes || 0);

      // Get overTime1.5 from API (raw minutes value)
      const overTime1_5 = parseFloat(item['overTime1.5'] || item.overTime1_5 || item.overTime15 || item.over_time_1_5 || item.over_time_15 || item.overtime1_5 || item.overtime15 || 0);

      const mapped = {
        employeeName: item.employeeName || item.employee_name || '-',
        employeeId: item.employeeId || item.employee_id || '-',
        userName: item.userName || item.username || item.user_name || '-',
        designation: item.designation || item.Designation || item.DESIGNATION || '-',
        machineType: item.machineType || item.machine_type || item.machineType || '-',
        totalPresent: parseInt(item.totalPresent || item.total_present || 0),
        totalAbsent: parseInt(item.totalAbsent || item.total_absent || 0),
        totalWorkTime: totalWorkTime, // Preserve as string with colon format
        totalOverTime: totalOverTime,  // Preserve as string with colon format
        totalAbsentHrsMins: totalAbsentHrsMins, // Total Absent Hrs and mins in HH:MM format
        totalAbsentDaysInMins: absentMins, // Total Absent Days in Minutes (raw value)
        totalOverMinutes: totalOverMinutes, // Total OT in Minutes (raw value)
        adjustedOT: adjustedOT, // Adjusted OT in HH:MM format
        overTime1_5: overTime1_5, // OT 1.5 Times in Minutes
        perHourRate: perHourRate, // Per hr pay
        otPay: otPay // OT Pay
      };

      return mapped;
    });

    // Sort by employee name
    const sorted = mappedData.sort((a, b) => {
      return (a.employeeName || '').localeCompare(b.employeeName || '');
    });

    console.log('Final mapped data:', sorted);
    console.log('=== mapMonthlyUserData END ===');
    return sorted;
  };

  // Helper function to map monthly API fields to UI fields
  // Maps: date, presentCount, absentCount, totalWorkTime, totalOverTime, totalMachineTime
  const mapMonthlyData = (apiData) => {
    console.log('=== mapMonthlyData START ===');
    console.log('Input apiData:', apiData);

    if (!Array.isArray(apiData) || apiData.length === 0) {
      console.log('mapMonthlyData: No data or not an array');
      return [];
    }

    console.log('Processing', apiData.length, 'items');

    // Directly map API response fields to UI fields
    const mappedData = apiData.map((item, index) => {
      // Map fields directly from API response
      // Handle both string and number values
      const mapped = {
        date: item.date || '',
        presentCount: parseInt(item.presentCount || 0),
        absentCount: parseInt(item.absentCount || 0),
        totalWorkTime: preserveTimeFormat(item.totalWorkTime || '00:00'),
        totalOverTime: preserveTimeFormat(item.totalOverTime || '00:00'),
        totalMachineTime: preserveTimeFormat(item.totalMachineTime || '00:00')
      };

      return mapped;
    });

    // Sort by date
    const sorted = mappedData.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });

    console.log('Final mapped data:', sorted);
    console.log('=== mapMonthlyData END ===');
    return sorted;
  };

  // Helper function to map yearly API fields to UI fields
  // Maps: monthName, presentCount, absentCount, totalWorkTime, totalOverTime, totalMachineTime
  const mapYearlyData = (apiData) => {
    console.log('=== mapYearlyData START ===');
    console.log('Input apiData:', apiData);

    if (!Array.isArray(apiData) || apiData.length === 0) {
      console.log('mapYearlyData: No data or not an array');
      return [];
    }

    console.log('Processing', apiData.length, 'items');

    // Directly map API response fields to UI fields
    const mappedData = apiData.map((item, index) => {
      // Map fields directly from API response
      const mapped = {
        month: item.monthName || item.month_name || item.month || '-',
        businessMonth: item.businessMonth || '',
        presentCount: parseInt(item.presentCount || 0),
        absentCount: parseInt(item.absentCount || 0),
        totalWorkTime: preserveTimeFormat(item.totalWorkTime || '00:00'),
        totalOverTime: preserveTimeFormat(item.totalOverTime || '00:00'),
        totalMachineTime: preserveTimeFormat(item.totalMachineTime || '00:00')
      };

      return mapped;
    });

    // Sort by businessMonth (YYYY-MM format) to maintain chronological order
    const sorted = mappedData.sort((a, b) => {
      const monthA = a.businessMonth || '';
      const monthB = b.businessMonth || '';
      return monthA.localeCompare(monthB);
    });

    console.log('Final mapped data:', sorted);
    console.log('=== mapYearlyData END ===');
    return sorted;
  };

  // Helper function to map yearly user API fields to UI fields
  // API Structure: Each item has employeeName at top level, and a "data" array with monthly entries
  // Maps: employeeName and monthly work time data from data array based on businessMonth and workTime fields
  const mapYearlyUserData = (apiData) => {
    console.log('=== mapYearlyUserData START ===');
    console.log('Input apiData:', apiData);
    console.log('Input apiData type:', Array.isArray(apiData) ? 'Array' : typeof apiData);

    if (!Array.isArray(apiData) || apiData.length === 0) {
      console.log('mapYearlyUserData: No data or not an array');
      return [];
    }

    console.log('Processing', apiData.length, 'employee items');

    // Log first item structure to understand the API format
    if (apiData.length > 0) {
      console.log('=== FIRST EMPLOYEE ITEM STRUCTURE ===');
      console.log('Full first item:', JSON.stringify(apiData[0], null, 2));
      console.log('First item keys:', Object.keys(apiData[0] || {}));
      console.log('Has data array?', Array.isArray(apiData[0].data));
      if (apiData[0].data && Array.isArray(apiData[0].data) && apiData[0].data.length > 0) {
        console.log('First data array item:', JSON.stringify(apiData[0].data[0], null, 2));
      }
    }

    // Month name to column key mapping - comprehensive list
    const monthNameMap = {
      // Full month names
      'january': 'jan',
      'february': 'feb',
      'march': 'mar',
      'april': 'apr',
      'may': 'may',
      'june': 'june',
      'july': 'jul',
      'august': 'aug',
      'september': 'sep',
      'october': 'oct',
      'november': 'nov',
      'december': 'dec',
      // Abbreviated month names
      'jan': 'jan',
      'feb': 'feb',
      'mar': 'mar',
      'apr': 'apr',
      'jun': 'june',
      'jul': 'jul',
      'aug': 'aug',
      'sep': 'sep',
      'oct': 'oct',
      'nov': 'nov',
      'dec': 'dec',
      // Month numbers (1-12)
      '1': 'jan',
      '01': 'jan',
      '2': 'feb',
      '02': 'feb',
      '3': 'mar',
      '03': 'mar',
      '4': 'apr',
      '04': 'apr',
      '5': 'may',
      '05': 'may',
      '6': 'june',
      '06': 'june',
      '7': 'jul',
      '07': 'jul',
      '8': 'aug',
      '08': 'aug',
      '9': 'sep',
      '09': 'sep',
      '10': 'oct',
      '11': 'nov',
      '12': 'dec'
    };

    // Helper function to normalize month name
    const normalizeMonthName = (monthName) => {
      if (!monthName) {
        return null;
      }
      const normalized = monthName.toString().toLowerCase().trim();
      return monthNameMap[normalized] || null;
    };

    // Helper function to convert workTime to hours
    const convertWorkTimeToHours = (workTime) => {
      if (workTime === null || workTime === undefined || workTime === '') {
        return 0;
      }
      if (typeof workTime === 'number') {
        return workTime;
      }
      if (typeof workTime === 'string' && workTime.includes(':')) {
        const parts = workTime.split(':');
        const hours = parseInt(parts[0], 10) || 0;
        const minutes = parseInt(parts[1], 10) || 0;
        return hours + (minutes / 60);
      }
      const parsed = parseFloat(workTime);
      return isNaN(parsed) ? 0 : parsed;
    };

    // Log detailed structure of first employee and their data array
    console.log('=== DETAILED API RESPONSE STRUCTURE ===');
    for (let i = 0; i < Math.min(2, apiData.length); i++) {
      const employeeItem = apiData[i];
      console.log(`\n--- Employee ${i} ---`);
      console.log('Full employee object:', JSON.stringify(employeeItem, null, 2));
      console.log('Employee keys:', Object.keys(employeeItem || {}));

      // Check employee name at top level
      const empName = employeeItem.employeeName || employeeItem.employee_name || employeeItem.name;
      console.log('✓ employeeName (top level):', empName);

      // Check if data array exists
      const monthlyData = employeeItem.data || employeeItem.monthlyData || employeeItem.months || [];
      console.log('✓ Has data array?', Array.isArray(monthlyData), '| Length:', monthlyData.length);

      if (Array.isArray(monthlyData) && monthlyData.length > 0) {
        console.log('\n  --- First Monthly Entry in Data Array ---');
        const firstMonth = monthlyData[0];
        console.log('  Full month object:', JSON.stringify(firstMonth, null, 2));
        console.log('  Month keys:', Object.keys(firstMonth || {}));

        // Check businessMonth in data array
        const busMonth = firstMonth.businessMonth || firstMonth.business_month || firstMonth.month || firstMonth.monthName;
        console.log('  ✓ businessMonth (in data array):', busMonth);

        // Check workTime in data array
        const wt = firstMonth.workTime || firstMonth.work_time || firstMonth.worktime;
        console.log('  ✓ workTime (in data array):', wt, '| Type:', typeof wt);

        // Test conversion
        if (wt) {
          const converted = convertWorkTimeToHours(wt);
          console.log(`  ✓ Conversion test: "${wt}" -> ${converted} hours`);
        } else {
          console.log('  ✗ workTime is missing in data array!');
        }

        // Test month normalization
        if (busMonth) {
          const monthKey = normalizeMonthName(busMonth);
          console.log(`  ✓ Month normalization: "${busMonth}" -> "${monthKey}"`);
          if (!monthKey) {
            console.log('  ✗ Month normalization FAILED!');
          }
        } else {
          console.log('  ✗ businessMonth is missing in data array!');
        }
      } else {
        console.log('  ✗ No data array found or data array is empty!');
      }
    }

    // Group data by employee name
    const employeeMap = {};

    // Track statistics for debugging
    let employeesProcessed = 0;
    let monthlyEntriesProcessed = 0;
    let itemsWithWorkTime = 0;
    let itemsMapped = 0;
    let itemsFailed = 0;

    // Loop through each employee in the array
    apiData.forEach((employeeItem, employeeIndex) => {
      employeesProcessed++;

      // Get employee name from top level
      const employeeName = (employeeItem.employeeName || employeeItem.employee_name || employeeItem.name || '-').toString().trim();

      // Check if this employee has a data array
      const monthlyData = employeeItem.data || employeeItem.monthlyData || employeeItem.months || [];

      if (!Array.isArray(monthlyData) || monthlyData.length === 0) {
        if (employeeIndex < 3) {
          console.log(`Employee ${employeeIndex} (${employeeName}) - No data array found. Keys:`, Object.keys(employeeItem));
        }
        return; // Skip this employee if no data array
      }

      // Initialize employee if not exists
      // Initialize with null/empty so we can store the exact API value
      if (!employeeMap[employeeName]) {
        employeeMap[employeeName] = {
          employeeName: employeeName,
          jan: null,  // Will be set from API - no default value
          feb: null,
          mar: null,
          apr: null,
          may: null,
          june: null,
          jul: null,
          aug: null,
          sep: null,
          oct: null,
          nov: null,
          dec: null,
          totalET: 0
        };
      }

      // Loop through monthly data array for this employee
      monthlyData.forEach((monthItem, monthIndex) => {
        monthlyEntriesProcessed++;

        // Get businessMonth from the data array item
        const businessMonth = monthItem.businessMonth || monthItem.business_month || monthItem.month || monthItem.monthName || monthItem.month_name || monthItem.Month;

        // Get workTime from the data array item (not from employee level)
        let workTime = null;
        let workTimeFieldName = null;

        // Try to find workTime in the monthItem
        if (monthItem.workTime !== undefined && monthItem.workTime !== null) {
          workTime = monthItem.workTime;
          workTimeFieldName = 'workTime';
          itemsWithWorkTime++;
        } else if (monthItem.work_time !== undefined && monthItem.work_time !== null) {
          workTime = monthItem.work_time;
          workTimeFieldName = 'work_time';
          itemsWithWorkTime++;
        } else if (monthItem.worktime !== undefined && monthItem.worktime !== null) {
          workTime = monthItem.worktime;
          workTimeFieldName = 'worktime';
          itemsWithWorkTime++;
        }

        // Log for first few entries
        if (employeeIndex < 3 && monthIndex < 3) {
          console.log(`Employee ${employeeIndex}, Month ${monthIndex}: ${employeeName} | businessMonth: "${businessMonth}" | workTime: "${workTime}"`);
        }

        // If workTime not found, default to 0
        if (workTime === null) {
          workTime = 0;
          if (employeeIndex < 3 && monthIndex < 2) {
            console.error(`Employee ${employeeIndex}, Month ${monthIndex} - workTime not found! Available keys:`, Object.keys(monthItem));
          }
        }

        // Map workTime to the corresponding month column based on businessMonth
        // Use workTime value EXACTLY as it comes from API - NO CONVERSION
        if (businessMonth) {
          const monthKey = normalizeMonthName(businessMonth);
          if (monthKey && employeeMap[employeeName][monthKey] !== undefined) {
            // Store workTime EXACTLY as it comes from API - no conversion, no formatting
            // If API returns "23:50", store "23:50"
            // If API returns "240", store "240"
            // If API returns 240, store 240
            // Just use the value directly
            employeeMap[employeeName][monthKey] = workTime;
            itemsMapped++;

            // Log for first few entries
            if (employeeIndex < 3 && monthIndex < 3) {
              console.log(`✅ Mapped: ${employeeName} | ${businessMonth} -> ${monthKey} | workTime from API: "${workTime}" (type: ${typeof workTime}) - stored directly without conversion`);
            }
          } else {
            itemsFailed++;
            if (employeeIndex < 3 && monthIndex < 2) {
              console.error(`❌ FAILED TO MAP: ${employeeName} | businessMonth: "${businessMonth}" -> monthKey: "${monthKey}"`);
              console.error(`  workTime: "${workTime}"`);
              console.error(`  Available month keys:`, Object.keys(employeeMap[employeeName] || {}));
            }
          }
        } else {
          itemsFailed++;
          if (employeeIndex < 3 && monthIndex < 2) {
            console.error(`❌ No businessMonth found for ${employeeName}!`);
            console.error(`  Month item keys:`, Object.keys(monthItem));
            console.error(`  Month item:`, JSON.stringify(monthItem, null, 2));
          }
        }
      }); // End of monthlyData.forEach

      // Also check for totalET from employee level (if available)
      if (employeeItem.totalET || employeeItem.total_et || employeeItem.totalElapsedTime || employeeItem.total_elapsed_time) {
        const etValue = parseFloat(employeeItem.totalET || employeeItem.total_et || employeeItem.totalElapsedTime || employeeItem.total_elapsed_time || 0);
        if (!isNaN(etValue)) {
          employeeMap[employeeName].totalET = etValue;
        }
      }
    }); // End of apiData.forEach

    // Log processing statistics
    console.log('=== PROCESSING STATISTICS ===');
    console.log('Total employees processed:', employeesProcessed);
    console.log('Total monthly entries processed:', monthlyEntriesProcessed);
    console.log('Monthly entries with workTime found:', itemsWithWorkTime);
    console.log('Monthly entries successfully mapped:', itemsMapped);
    console.log('Monthly entries failed to map:', itemsFailed);

    if (itemsWithWorkTime === 0) {
      console.error('⚠️ CRITICAL: No workTime values were found in ANY monthly entries!');
      console.error('This means the field name in API response data array is different than expected.');
      console.error('Check the "FIRST EMPLOYEE ITEM STRUCTURE" section above to see actual field names.');
    }

    if (itemsMapped === 0 && itemsWithWorkTime > 0) {
      console.error('⚠️ CRITICAL: workTime values were found but NOT mapped to month columns!');
      console.error('This means businessMonth field is missing or not matching correctly.');
      console.error('Check the employee/month logs above to see businessMonth values.');
    }

    // Log employee map before conversion
    console.log('=== EMPLOYEE MAP BEFORE CONVERSION ===');
    Object.keys(employeeMap).slice(0, 5).forEach(empName => {
      console.log(`${empName}:`, employeeMap[empName]);
    });

    // Convert map to array and calculate totals
    const mappedData = Object.values(employeeMap).map((employee) => {
      // Calculate total hours (sum of all months) - convert each month to hours, sum, then convert back to "HH:MM"
      let totalHours = 0;
      const months = ['jan', 'feb', 'mar', 'apr', 'may', 'june', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      months.forEach(month => {
        totalHours += convertWorkTimeToHours(employee[month] || '00:00');
      });

      // Convert total hours back to "HH:MM" format
      const hours = Math.floor(totalHours);
      const minutes = Math.round((totalHours - hours) * 60);
      const totalHrs = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

      return {
        ...employee,
        totalHrs: totalHrs
      };
    });

    // Sort by employee name
    const sorted = mappedData.sort((a, b) => {
      return (a.employeeName || '').localeCompare(b.employeeName || '');
    });

    // Log summary of all employees
    console.log('=== MAPPED DATA SUMMARY ===');
    console.log('Total employees mapped:', sorted.length);
    let employeesWithData = 0;
    sorted.forEach((emp, idx) => {
      const hasData = emp.jan > 0 || emp.feb > 0 || emp.mar > 0 || emp.apr > 0 ||
        emp.may > 0 || emp.june > 0 || emp.jul > 0 || emp.aug > 0 ||
        emp.sep > 0 || emp.oct > 0 || emp.nov > 0 || emp.dec > 0;
      if (hasData) {
        employeesWithData++;
      }
      if (hasData || idx < 10) {
        console.log(`Employee ${idx + 1}: ${emp.employeeName}`, {
          jan: emp.jan,
          feb: emp.feb,
          mar: emp.mar,
          apr: emp.apr,
          may: emp.may,
          june: emp.june,
          jul: emp.jul,
          aug: emp.aug,
          sep: emp.sep,
          oct: emp.oct,
          nov: emp.nov,
          dec: emp.dec,
          totalHrs: emp.totalHrs
        });
      }
    });

    // Alert if no data was mapped
    if (employeesWithData === 0 && sorted.length > 0) {
      console.error('⚠️ WARNING: No workTime data was mapped! All values are 0.');
      console.error('Check the console logs above to see why mapping failed.');
      console.error('Common issues:');
      console.error('1. workTime field name might be different in API');
      console.error('2. businessMonth format might not match');
      console.error('3. Check "RAW API RESPONSE - YEARLY USER" section above');
    }

    // Log specifically for Amrit Singh
    const amritData = sorted.find(emp =>
      emp.employeeName && emp.employeeName.toLowerCase().includes('amrit') &&
      emp.employeeName.toLowerCase().includes('singh')
    );
    if (amritData) {
      console.log('=== AMRIT SINGH MAPPED DATA ===');
      console.log('Full data:', JSON.stringify(amritData, null, 2));
      console.log('January value:', amritData.jan);
      console.log('All months:', {
        jan: amritData.jan,
        feb: amritData.feb,
        mar: amritData.mar,
        apr: amritData.apr,
        may: amritData.may,
        june: amritData.june,
        jul: amritData.jul,
        aug: amritData.aug,
        sep: amritData.sep,
        oct: amritData.oct,
        nov: amritData.nov,
        dec: amritData.dec
      });
    } else {
      console.log('=== AMRIT SINGH NOT FOUND IN MAPPED DATA ===');
    }

    console.log('Final mapped data:', sorted);
    console.log('=== mapYearlyUserData END ===');
    return sorted;
  };

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      setError(null);

      // API URL for attendance - from config
      const attendanceApiUrl = config.attendanceApiUrl;

      // Prepare request payload based on active tab
      let requestPayload = {};

      if (activeTab === 0) {
        // Daily attendance - send date
        requestPayload = { date: selectedDate };
      } else if (activeTab === 1) {
        // Monthly attendance - send month (YYYY-MM format)
        requestPayload = { month: selectedMonth };
      } else if (activeTab === 2) {
        // Monthly user attendance - send month (YYYY-MM format)
        requestPayload = { month: selectedMonth };
      } else if (activeTab === 3) {
        // Yearly attendance - send year
        requestPayload = { year: selectedYear };
      } else if (activeTab === 4) {
        // Yearly user attendance - send year
        requestPayload = { year: selectedYear };
      }

      // Fetch data from API
      const response = await axios.post(
        attendanceApiUrl,
        requestPayload,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      ).catch((err) => {
        console.error('API Error:', err);
        return null;
      });

      if (response && response.data) {
        // Check if response has status field
        const responseData = response.data;

        // Log API response for debugging - FULL RESPONSE
        console.log('=== ATTENDANCE API RESPONSE ===');
        console.log('Full Response Data:', JSON.stringify(responseData, null, 2));
        console.log('Response Keys:', Object.keys(responseData));
        console.log('Response Status:', responseData.status);

        const isSuccess = responseData.status === 'success' || responseData.status === 'Success' ||
          responseData.status === 'SUCCESS' || !responseData.status ||
          (responseData.daily || responseData.monthly || responseData.yearly);

        if (isSuccess) {
          // Map API response arrays to component state based on tab
          if (activeTab === 0) {
            // Map daily array
            const dailyArray = responseData.daily || responseData.dailyData || responseData.data || [];
            console.log('Daily Array from API:', dailyArray);
            const mappedDaily = mapDailyData(dailyArray);
            console.log('Mapped Daily Data:', mappedDaily);
            setDailyData(mappedDaily);
          } else if (activeTab === 1) {
            // Map monthly data - can be object or array
            console.log('=== MONTHLY TAB - EXTRACTING DATA ===');
            console.log('responseData.monthly:', responseData.monthly);
            console.log('Monthly type:', typeof responseData.monthly);

            const monthlyData = responseData.monthly;

            if (!monthlyData) {
              console.warn('Monthly data not found in response');
              setMonthlyData([]);
            } else if (Array.isArray(monthlyData)) {
              // If it's already an array, map it directly
              console.log('Monthly is an array, length:', monthlyData.length);
              const mappedMonthly = mapMonthlyData(monthlyData);
              setMonthlyData(mappedMonthly);
            } else if (typeof monthlyData === 'object') {
              // If it's an object with date keys, convert to array
              console.log('Monthly is an object, converting to array');
              const monthlyArray = Object.keys(monthlyData).map(date => ({
                date: date,
                ...monthlyData[date]
              }));
              console.log('Converted monthly array:', monthlyArray);
              const mappedMonthly = mapMonthlyData(monthlyArray);
              console.log('Mapped Monthly Data:', mappedMonthly);
              setMonthlyData(mappedMonthly);
            } else {
              console.warn('Monthly data is not in expected format');
              setMonthlyData([]);
            }
          } else if (activeTab === 2) {
            // Map monthlyuser array - directly from API response
            console.log('=== MONTHLY USER TAB - EXTRACTING DATA ===');
            console.log('responseData.monthlyuser:', responseData.monthlyuser);

            const monthlyUserArray = responseData.monthlyuser || responseData.monthlyUser || [];
            console.log('Monthly User Array:', monthlyUserArray);
            console.log('Monthly User Array type:', Array.isArray(monthlyUserArray) ? 'Array' : typeof monthlyUserArray);
            console.log('Monthly User Array length:', Array.isArray(monthlyUserArray) ? monthlyUserArray.length : 'N/A');

            if (Array.isArray(monthlyUserArray) && monthlyUserArray.length > 0) {
              console.log('First item in monthlyuser array:', monthlyUserArray[0]);
              const mappedMonthlyUser = mapMonthlyUserData(monthlyUserArray);
              console.log('Mapped Monthly User Data:', mappedMonthlyUser);
              setMonthlyUserData(mappedMonthlyUser);
            } else {
              console.warn('Monthly user array is empty or not found');
              setMonthlyUserData([]);
            }
          } else if (activeTab === 3) {
            // Map yearly array - directly from API response
            console.log('=== YEARLY TAB - EXTRACTING DATA ===');
            console.log('responseData.yearly:', responseData.yearly);

            const yearlyArray = responseData.yearly || [];
            console.log('Yearly Array:', yearlyArray);
            console.log('Yearly Array type:', Array.isArray(yearlyArray) ? 'Array' : typeof yearlyArray);
            console.log('Yearly Array length:', Array.isArray(yearlyArray) ? yearlyArray.length : 'N/A');

            if (Array.isArray(yearlyArray) && yearlyArray.length > 0) {
              console.log('First item in yearly array:', yearlyArray[0]);
              const mappedYearly = mapYearlyData(yearlyArray);
              console.log('Mapped Yearly Data:', mappedYearly);
              setYearlyData(mappedYearly);
            } else {
              console.warn('Yearly array is empty or not found');
              setYearlyData([]);
            }
          } else if (activeTab === 4) {
            // Map yearlyuser array - directly from API response
            console.log('=== YEARLY USER TAB - EXTRACTING DATA ===');
            console.log('responseData.yearlyuser:', responseData.yearlyuser);

            const yearlyUserArray = responseData.yearlyuser || responseData.yearlyUser || [];
            console.log('Yearly User Array:', yearlyUserArray);
            console.log('Yearly User Array type:', Array.isArray(yearlyUserArray) ? 'Array' : typeof yearlyUserArray);
            console.log('Yearly User Array length:', Array.isArray(yearlyUserArray) ? yearlyUserArray.length : 'N/A');

            if (Array.isArray(yearlyUserArray) && yearlyUserArray.length > 0) {
              console.log('=== RAW API RESPONSE - YEARLY USER ===');
              console.log('Total items:', yearlyUserArray.length);
              console.log('First item (full):', JSON.stringify(yearlyUserArray[0], null, 2));
              console.log('First item keys:', Object.keys(yearlyUserArray[0] || {}));

              // Show first 3 items with all their fields
              console.log('First 3 items detailed:');
              yearlyUserArray.slice(0, 3).forEach((item, idx) => {
                console.log(`\n--- Item ${idx} ---`);
                console.log('All fields:', Object.keys(item));
                console.log('Values:', {
                  employeeName: item.employeeName,
                  employee_name: item.employee_name,
                  name: item.name,
                  businessMonth: item.businessMonth,
                  business_month: item.business_month,
                  month: item.month,
                  monthName: item.monthName,
                  workTime: item.workTime,
                  work_time: item.work_time,
                  worktime: item.worktime,
                  totalWorkTime: item.totalWorkTime,
                  total_work_time: item.total_work_time
                });
              });

              const mappedYearlyUser = mapYearlyUserData(yearlyUserArray);
              console.log('=== FINAL MAPPED DATA ===');
              console.log('Mapped Yearly User Data (first 3):', mappedYearlyUser.slice(0, 3));
              setYearlyUserData(mappedYearlyUser);
            } else {
              console.warn('Yearly user array is empty or not found');
              setYearlyUserData([]);
            }
          }
        } else {
          // API returned error status
          setError(responseData.message || 'Failed to fetch attendance data. Showing sample data...');
          loadSampleData();
        }
      } else {
        // No response or invalid response
        setError('No data received from API. Showing sample data...');
        loadSampleData();
      }
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      setError(`Error: ${err.message || 'Failed to fetch attendance data. Showing sample data...'}`);
      loadSampleData();
    } finally {
      setLoading(false);
    }
  };

  const loadSampleData = () => {
    if (activeTab === 0) {
      // Sample daily attendance data
      const sampleDaily = [
        { name: 'John Doe', present: 1, absent: 0, regularHours: 8, otHours: 1, total: 9 },
        { name: 'Jane Smith', present: 1, absent: 0, regularHours: 8, otHours: 0.5, total: 8.5 },
        { name: 'Mike Johnson', present: 1, absent: 0, regularHours: 8, otHours: 1, total: 9 },
        { name: 'Sarah Williams', present: 0, absent: 1, regularHours: 0, otHours: 0, total: 0 },
        { name: 'Rahul Kumar', present: 1, absent: 0, regularHours: 8, otHours: 0, total: 8 },
        { name: 'Priya Sharma', present: 1, absent: 0, regularHours: 8, otHours: 0.5, total: 8.5 },
        { name: 'Amit Patel', present: 1, absent: 0, regularHours: 8, otHours: 1, total: 9 }
      ];
      setDailyData(sampleDaily);
    } else if (activeTab === 1) {
      // Sample monthly attendance data - generate data for the selected month with correct structure
      const [year, month] = selectedMonth.split('-');
      const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
      const sampleMonthly = [];

      for (let day = 1; day <= Math.min(daysInMonth, 10); day++) {
        const dateStr = `${year}-${month}-${String(day).padStart(2, '0')}`;
        const isPresent = day % 7 !== 0;
        sampleMonthly.push({
          date: dateStr,
          presentCount: isPresent ? 1 : 0,
          absentCount: isPresent ? 0 : 1,
          totalWorkTime: isPresent ? 8.0 : 0.0,
          totalOverTime: day % 3 === 0 ? 1.0 : 0.5,
          totalMachineTime: isPresent ? 7.5 : 0.0
        });
      }
      console.log('Sample Monthly Data:', sampleMonthly);
      setMonthlyData(sampleMonthly);
    } else if (activeTab === 2) {
      // Sample monthly user attendance data
      const sampleMonthlyUser = [
        { employeeName: 'Anu Test', employeeId: '177', userName: 'LS3002', totalPresent: 1, totalAbsent: 19, totalWorkTime: 9.6, totalOverTime: 9 },
        { employeeName: 'John Doe', employeeId: '178', userName: 'LS3003', totalPresent: 15, totalAbsent: 5, totalWorkTime: 120.5, totalOverTime: 10.5 },
        { employeeName: 'Jane Smith', employeeId: '179', userName: 'LS3004', totalPresent: 18, totalAbsent: 2, totalWorkTime: 144.0, totalOverTime: 8.0 }
      ];
      setMonthlyUserData(sampleMonthlyUser);
    } else if (activeTab === 3) {
      // Sample yearly attendance data
      const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      const names = ['John Doe', 'Jane Smith', 'Mike Johnson'];
      const sampleYearly = months.map((month, index) => ({
        month: month,
        name: names[index % names.length],
        totalHours: 160 + Math.floor(Math.random() * 40),
        totalAbsent: Math.floor(Math.random() * 10),
        totalRegularHours: 150 + Math.floor(Math.random() * 30),
        totalOTHours: 10 + Math.floor(Math.random() * 20)
      }));
      setYearlyData(sampleYearly);
    } else if (activeTab === 4) {
      // Sample yearly user attendance data
      const sampleYearlyUser = [
        { employeeName: 'John Doe', employeeId: '101', userName: 'LS1001', totalPresent: 240, totalAbsent: 10, totalWorkTime: 1920.5, totalOverTime: 120.5, totalMachineTime: 1900.0 },
        { employeeName: 'Jane Smith', employeeId: '102', userName: 'LS1002', totalPresent: 235, totalAbsent: 15, totalWorkTime: 1880.0, totalOverTime: 110.0, totalMachineTime: 1850.0 },
        { employeeName: 'Mike Johnson', employeeId: '103', userName: 'LS1003', totalPresent: 245, totalAbsent: 5, totalWorkTime: 1960.0, totalOverTime: 130.0, totalMachineTime: 1950.0 }
      ];
      setYearlyUserData(sampleYearlyUser);
    }
  };

  // Calculate totals for daily data
  const dailyTotals = dailyData.reduce((acc, item) => ({
    present: acc.present + (item.present || 0),
    absent: acc.absent + (item.absent || 0),
    workTime: acc.workTime + convertWorkTimeToHours(item.workTime || item.regularHours || '00:00'),
    overTime: acc.overTime + convertWorkTimeToHours(item.overTime || item.otHours || '00:00'),
    machineTime: acc.machineTime + convertWorkTimeToHours(item.machineTime || '00:00'),
    regularHours: acc.regularHours + convertWorkTimeToHours(item.workTime || item.regularHours || '00:00'),
    otHours: acc.otHours + convertWorkTimeToHours(item.overTime || item.otHours || '00:00'),
    total: acc.total + (convertWorkTimeToHours(item.workTime || item.regularHours || '00:00') + convertWorkTimeToHours(item.overTime || item.otHours || '00:00'))
  }), { present: 0, absent: 0, workTime: 0, overTime: 0, machineTime: 0, regularHours: 0, otHours: 0, total: 0 });

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Export monthly attendance data to Excel
  const exportMonthlyDataToExcel = () => {
    if (monthlyData.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare data for Excel export
    const excelData = monthlyData.map((row, index) => ({
      '#': index + 1,
      'Date': row.date || '-',
      'Present Count': row.presentCount || 0,
      'Absent Count': row.absentCount || 0,
      'Total Work Time': row.totalWorkTime || '00:00',
      'Total Over Time': row.totalOverTime || '00:00',
      'Total Machine Time': row.totalMachineTime || '00:00'
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Create a worksheet from the data
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 5 },   // #
      { wch: 15 },  // Date
      { wch: 15 },  // Present Count
      { wch: 15 },  // Absent Count
      { wch: 18 },  // Total Work Time
      { wch: 18 },  // Total Over Time
      { wch: 20 }   // Total Machine Time
    ];

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Monthly Attendance');

    // Generate Excel file and download
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const fileName = `monthly_attendance_${selectedMonth.replace(/-/g, '_')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export monthly user attendance data to Excel
  const exportMonthlyUserDataToExcel = () => {
    if (monthlyUserData.length === 0) {
      alert('No data to export');
      return;
    }

    // Filter monthly user data based on selected filters
    const filteredMonthlyUserData = monthlyUserData.filter(row => {
      const designationMatch = selectedDesignation === 'all' || row.designation === selectedDesignation;
      const machineTypeMatch = selectedMachineType === 'all' || row.machineType === selectedMachineType;
      return designationMatch && machineTypeMatch;
    });

    if (filteredMonthlyUserData.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare data for Excel export
    const excelData = filteredMonthlyUserData.map((row, index) => ({
      '#': index + 1,
      'Employee Name': row.employeeName || '-',
      'Username': row.userName || '-',
      'Designation': row.designation || '-',
      'Machine Type': row.machineType || '-',
      'Total Present (Days)': row.totalPresent || 0,
      'Total Absent (Days)': row.totalAbsent || 0,
      'Total Absent Days(HH:MM)': row.totalAbsentHrsMins || '00:00',
      'Total Absent Days (Minutes)': row.totalAbsentDaysInMins || 0,
      'Total Work Time (HH:MM)': row.totalWorkTime || '00:00',
      'Total Over Time (HH:MM)': row.totalOverTime || '00:00',
      'Total OT (Minutes)': row.totalOverMinutes || 0,
      'Adjusted OT (HH : MM)': row.adjustedOT || '00:00',
      'OT 1.5 Times((Minutes)': row.overTime1_5 || 0,
      'Per Hr Pay (Rupees)': row.perHourRate || 0,
      'OT Pay (Rupees)': row.otPay || 0
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Create a worksheet from the data
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 5 },   // #
      { wch: 20 },  // Employee Name
      { wch: 15 },  // Username
      { wch: 15 },  // Designation
      { wch: 15 },  // Machine Type
      { wch: 20 },  // Total Present (Days)
      { wch: 20 },  // Total Absent (Days)
      { wch: 25 },  // Total Absent Days(HH:MM)
      { wch: 25 },  // Total Absent Days (Minutes)
      { wch: 22 },  // Total Work Time (HH:MM)
      { wch: 22 },  // Total Over Time (HH:MM)
      { wch: 20 },  // Total OT (Minutes)
      { wch: 22 },  // Adjusted OT (HH : MM)
      { wch: 25 },  // OT 1.5 Times((Minutes)
      { wch: 20 },  // Per Hr Pay (Rupees)
      { wch: 20 }   // OT Pay (Rupees)
    ];

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Monthly User Attendance');

    // Generate Excel file and download
    const fileName = `monthly_user_attendance_${selectedMonth.replace(/-/g, '_')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export yearly attendance data to Excel
  const exportYearlyDataToExcel = () => {
    if (yearlyData.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare data for Excel export
    const excelData = yearlyData.map((row, index) => ({
      '#': index + 1,
      'Month': row.month || '-',
      'Present Count': row.presentCount || 0,
      'Absent Count': row.absentCount || 0,
      'Total Work Time': row.totalWorkTime || '00:00',
      'Total Over Time': row.totalOverTime || '00:00',
      'Total Machine Time': row.totalMachineTime || '00:00'
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Create a worksheet from the data
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 5 },   // #
      { wch: 15 },  // Month
      { wch: 15 },  // Present Count
      { wch: 15 },  // Absent Count
      { wch: 18 },  // Total Work Time
      { wch: 18 },  // Total Over Time
      { wch: 20 }   // Total Machine Time
    ];

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Yearly Attendance');

    // Generate Excel file and download
    const fileName = `yearly_attendance_${selectedYear}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export yearly user attendance data to Excel
  const exportYearlyUserDataToExcel = () => {
    if (yearlyUserData.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare data for Excel export - preserve original format
    const excelData = yearlyUserData.map((row, index) => ({
      'Name': row.employeeName || '-',
      'Jan': row.jan || '00:00',
      'Feb': row.feb || '00:00',
      'Mar': row.mar || '00:00',
      'Apr': row.apr || '00:00',
      'May': row.may || '00:00',
      'June': row.june || '00:00',
      'Jul': row.jul || '00:00',
      'Aug': row.aug || '00:00',
      'Sep': row.sep || '00:00',
      'Oct': row.oct || '00:00',
      'Nov': row.nov || '00:00',
      'Dec': row.dec || '00:00',
      'Total Hrs': row.totalHrs || '00:00',
      'Total ET': Math.round(row.totalET || 0)
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Create a worksheet from the data
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 20 },  // Name
      { wch: 10 },  // Jan
      { wch: 10 },  // Feb
      { wch: 10 },  // Mar
      { wch: 10 },  // Apr
      { wch: 10 },  // May
      { wch: 10 },  // June
      { wch: 10 },  // Jul
      { wch: 10 },  // Aug
      { wch: 10 },  // Sep
      { wch: 10 },  // Oct
      { wch: 10 },  // Nov
      { wch: 10 },  // Dec
      { wch: 12 },  // Total Hrs
      { wch: 12 }   // Total ET
    ];

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Yearly User Attendance');

    // Generate Excel file and download
    const fileName = `yearly_user_attendance_${selectedYear}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export daily attendance data to Excel
  const exportDailyDataToExcel = () => {
    // Filter daily data based on selected filters
    const filteredDailyData = dailyData.filter(row => {
      const nameMatch = selectedName === 'all' || (row.employeeName || row.name) === selectedName;
      const designationMatch = selectedDesignation === 'all' || row.designation === selectedDesignation;
      const machineTypeMatch = selectedMachineType === 'all' || row.machineType === selectedMachineType;
      // Filter by present status
      let presentMatch = true;
      if (selectedPresent === 'yes') {
        presentMatch = row.present === 1;
      } else if (selectedPresent === 'no') {
        presentMatch = row.present === 0 || row.absent === 1;
      }
      return nameMatch && designationMatch && machineTypeMatch && presentMatch;
    });

    if (filteredDailyData.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare data for Excel export
    const excelData = filteredDailyData.map((row, index) => ({
      '#': index + 1,
      'Name': row.employeeName || row.name || '-',
      'Username': row.userName || '-',
      'Machine Type': row.machineType || '-',
      'Designation': row.designation || '-',
      'Present': row.present === 1 ? 'Yes' : 'No',
      'Work Time (Hrs)': formatHoursWithColon(row.workTime || row.regularHours || 0),
      'Over Time (Hrs)': formatHoursWithColon(row.overTime || row.otHours || 0),
      'Over Time 1.5 Times (Mins)': row.overTime1_5 || 0,
      'Per Hour Rate (Rs.)': row.perHourRate || 0,
      'Over Time Pay (Rs.)': row.overTimePa || 0,
      'Machine Time': row.machineTime || 0
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Create a worksheet from the data
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 5 },   // #
      { wch: 20 },  // Name
      { wch: 15 },  // Username
      { wch: 15 },  // Machine Type
      { wch: 15 },  // Designation
      { wch: 10 },  // Present
      { wch: 18 },  // Work Time (Hrs)
      { wch: 18 },  // Over Time (Hrs)
      { wch: 25 },  // Over Time 1.5 Times (Mins)
      { wch: 18 },  // Per Hour Rate (Rs.)
      { wch: 18 },  // Over Time Pay (Rs.)
      { wch: 15 }   // Machine Time
    ];

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Attendance');

    // Generate Excel file and download
    const fileName = `daily_attendance_${formatDate(selectedDate).replace(/-/g, '_')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export By Operator attendance data to Excel
  const exportByOperatorDataToExcel = () => {
    if (byOperatorData.length === 0 && byDateData.length === 0) {
      alert('No data to export');
      return;
    }

    const daysInMonth = getDaysInMonth(byOperatorMonth);
    const wb = XLSX.utils.book_new();

    // Prepare header row with all columns
    const headers = [
      'Name',
      'Desig',
      'User ID',
      'Total Present',
      'Total Absence',
      '% of Present',
      '% of Absence',
      'Total Work Time in Mins',
      'Total OT (in minutes)',
      'Total OT Mins (1.5 Times)',
      'OT % on regular',
      'Date Of',
      'Total Work (in Hrs)',
      'Total OT (in Hrs)',
      ...Array.from({ length: daysInMonth }, (_, i) => `Day ${i + 1}`)
    ];

    // Prepare summary rows data
    const summaryRows = [];

    // Total Present row
    const totalPresentRow = {
      'Name': 'Total Present',
      'Desig': '',
      'User ID': '',
      'Total Present': '',
      'Total Absence': '',
      '% of Present': '',
      '% of Absence': '',
      'Total Work Time in Mins': '',
      'Total OT (in minutes)': '',
      'Total OT Mins (1.5 Times)': '',
      'OT % on regular': '',
      'Date Of': '',
      'Total Work (in Hrs)': '',
      'Total OT (in Hrs)': ''
    };
    for (let day = 1; day <= daysInMonth; day++) {
      const dateItem = byDateData.find(item => item.day === day);
      const value = dateItem ? dateItem.totalPresent :
        byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day] === 'P') return sum + 1;
          return sum;
        }, 0);
      totalPresentRow[`Day ${day}`] = value;
    }
    summaryRows.push(totalPresentRow);

    // Total Absence row
    const totalAbsenceRow = {
      'Name': 'Total Absence',
      'Desig': '',
      'User ID': '',
      'Total Present': '',
      'Total Absence': '',
      '% of Present': '',
      '% of Absence': '',
      'Total Work Time in Mins': '',
      'Total OT (in minutes)': '',
      'Total OT Mins (1.5 Times)': '',
      'OT % on regular': '',
      'Date Of': '',
      'Total Work (in Hrs)': '',
      'Total OT (in Hrs)': ''
    };
    for (let day = 1; day <= daysInMonth; day++) {
      const dateItem = byDateData.find(item => item.day === day);
      const value = dateItem ? dateItem.totalAbsent :
        byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day] === 'A') return sum + 1;
          return sum;
        }, 0);
      totalAbsenceRow[`Day ${day}`] = value;
    }
    summaryRows.push(totalAbsenceRow);

    // % of Present row
    const percentPresentRow = {
      'Name': '% of Present',
      'Desig': '',
      'User ID': '',
      'Total Present': '',
      'Total Absence': '',
      '% of Present': '',
      '% of Absence': '',
      'Total Work Time in Mins': '',
      'Total OT (in minutes)': '',
      'Total OT Mins (1.5 Times)': '',
      'OT % on regular': '',
      'Date Of': '',
      'Total Work (in Hrs)': '',
      'Total OT (in Hrs)': ''
    };
    for (let day = 1; day <= daysInMonth; day++) {
      const dateItem = byDateData.find(item => item.day === day);
      let percent = '0';
      if (dateItem) {
        percent = typeof dateItem.percentageOfPresence === 'number'
          ? String(Math.ceil(dateItem.percentageOfPresence))
          : String(Math.ceil(parseFloat((dateItem.percentageOfPresence || '0').toString().replace('%', '')) || 0));
      } else {
        const dayPresent = byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day] === 'P') return sum + 1;
          return sum;
        }, 0);
        const dayAbsent = byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day] === 'A') return sum + 1;
          return sum;
        }, 0);
        const total = dayPresent + dayAbsent;
        percent = total > 0 ? String(Math.ceil((dayPresent / total) * 100)) : '0';
      }
      percentPresentRow[`Day ${day}`] = percent + '%';
    }
    summaryRows.push(percentPresentRow);

    // % of Absence row
    const percentAbsenceRow = {
      'Name': '% of Absence',
      'Desig': '',
      'User ID': '',
      'Total Present': '',
      'Total Absence': '',
      '% of Present': '',
      '% of Absence': '',
      'Total Work Time in Mins': '',
      'Total OT (in minutes)': '',
      'Total OT Mins (1.5 Times)': '',
      'OT % on regular': '',
      'Date Of': '',
      'Total Work (in Hrs)': '',
      'Total OT (in Hrs)': ''
    };
    for (let day = 1; day <= daysInMonth; day++) {
      const dateItem = byDateData.find(item => item.day === day);
      let percent = '0';
      if (dateItem) {
        percent = typeof dateItem.percentageOfAbsence === 'number'
          ? String(Math.ceil(dateItem.percentageOfAbsence))
          : String(Math.ceil(parseFloat((dateItem.percentageOfAbsence || '0').toString().replace('%', '')) || 0));
      } else {
        const dayPresent = byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day] === 'P') return sum + 1;
          return sum;
        }, 0);
        const dayAbsent = byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day] === 'A') return sum + 1;
          return sum;
        }, 0);
        const total = dayPresent + dayAbsent;
        percent = total > 0 ? String(Math.ceil((dayAbsent / total) * 100)) : '0';
      }
      percentAbsenceRow[`Day ${day}`] = percent + '%';
    }
    summaryRows.push(percentAbsenceRow);

    // Total OT Mins row
    const totalOTMinsRow = {
      'Name': 'Total OT Mins',
      'Desig': '',
      'User ID': '',
      'Total Present': '',
      'Total Absence': '',
      '% of Present': '',
      '% of Absence': '',
      'Total Work Time in Mins': '',
      'Total OT (in minutes)': '',
      'Total OT Mins (1.5 Times)': '',
      'OT % on regular': '',
      'Date Of': '',
      'Total Work (in Hrs)': '',
      'Total OT (in Hrs)': ''
    };
    for (let day = 1; day <= daysInMonth; day++) {
      const dateItem = byDateData.find(item => item.day === day);
      const value = dateItem ? dateItem.OTMins :
        byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day]) {
            return sum + Math.floor((user.OTMins || 0) / daysInMonth);
          }
          return sum;
        }, 0);
      totalOTMinsRow[`Day ${day}`] = value || '';
    }
    summaryRows.push(totalOTMinsRow);

    // OT % row
    const otPercentRow = {
      'Name': 'OT %',
      'Desig': '',
      'User ID': '',
      'Total Present': '',
      'Total Absence': '',
      '% of Present': '',
      '% of Absence': '',
      'Total Work Time in Mins': '',
      'Total OT (in minutes)': '',
      'Total OT Mins (1.5 Times)': '',
      'OT % on regular': '',
      'Date Of': '',
      'Total Work (in Hrs)': '',
      'Total OT (in Hrs)': ''
    };
    for (let day = 1; day <= daysInMonth; day++) {
      const dateItem = byDateData.find(item => item.day === day);
      let percent = '0';
      if (dateItem) {
        percent = typeof dateItem.OTPercentage === 'number'
          ? String(Math.ceil(dateItem.OTPercentage))
          : String(Math.ceil(parseFloat((dateItem.OTPercentage || '0').toString().replace('%', '')) || 0));
      } else {
        const dayOTMins = byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day]) {
            return sum + Math.floor((user.OTMins || 0) / daysInMonth);
          }
          return sum;
        }, 0);
        const totalWorkMins = byOperatorData.length * 8 * 60;
        percent = totalWorkMins > 0 ? String(Math.ceil((dayOTMins / totalWorkMins) * 100)) : '0';
      }
      otPercentRow[`Day ${day}`] = percent + '%';
    }
    summaryRows.push(otPercentRow);

    // Total work (in Hrs) row
    const totalWorkHrsRow = {
      'Name': 'Total work (in Hrs)',
      'Desig': '',
      'User ID': '',
      'Total Present': '',
      'Total Absence': '',
      '% of Present': '',
      '% of Absence': '',
      'Total Work Time in Mins': '',
      'Total OT (in minutes)': '',
      'Total OT Mins (1.5 Times)': '',
      'OT % on regular': '',
      'Date Of': '',
      'Total Work (in Hrs)': '',
      'Total OT (in Hrs)': ''
    };
    for (let day = 1; day <= daysInMonth; day++) {
      const dateItem = byDateData.find(item => item.day === day);
      const value = dateItem ? (dateItem.workTimeinHrs || 0) :
        byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day]) {
            return sum + (user.workTimeinHrs || 0);
          }
          return sum;
        }, 0);
      totalWorkHrsRow[`Day ${day}`] = value || '';
    }
    summaryRows.push(totalWorkHrsRow);

    // Total OT (in Hrs) row
    const totalOTHrsRow = {
      'Name': 'Total OT (in Hrs)',
      'Desig': '',
      'User ID': '',
      'Total Present': '',
      'Total Absence': '',
      '% of Present': '',
      '% of Absence': '',
      'Total Work Time in Mins': '',
      'Total OT (in minutes)': '',
      'Total OT Mins (1.5 Times)': '',
      'OT % on regular': '',
      'Date Of': '',
      'Total Work (in Hrs)': '',
      'Total OT (in Hrs)': ''
    };
    for (let day = 1; day <= daysInMonth; day++) {
      const dateItem = byDateData.find(item => item.day === day);
      const value = dateItem ? (dateItem.OTHrs || 0) :
        byOperatorData.reduce((sum, user) => {
          if (user.attendance && user.attendance[day]) {
            return sum + (user.OTHrs || 0);
          }
          return sum;
        }, 0);
      totalOTHrsRow[`Day ${day}`] = value || '';
    }
    summaryRows.push(totalOTHrsRow);

    // Prepare user rows data
    const userRows = byOperatorData.map((user) => {
      const userName = user.name || '-';
      const userDesignation = user.designation || '-';
      const userId = user.userName || user.employeeId || '-';
      const totalPresent = user.totalPresent || user.byOperator?.totalPresent || 0;
      const totalAbsent = user.totalAbsent || user.byOperator?.totalAbsent || 0;

      let presencePercent = user.percentageOfPresence || user.byOperator?.percentageOfPresence || '0%';
      if (typeof presencePercent === 'number') {
        presencePercent = Math.ceil(presencePercent) + '%';
      } else if (!presencePercent.toString().includes('%')) {
        presencePercent = presencePercent + '%';
      }

      let absencePercent = user.percentageOfAbsence || user.byOperator?.percentageOfAbsence || '0%';
      if (typeof absencePercent === 'number') {
        absencePercent = Math.ceil(absencePercent) + '%';
      } else if (!absencePercent.toString().includes('%')) {
        absencePercent = absencePercent + '%';
      }

      const workTimeinMins = user.workTimeinMins || user.workTimeInMins || user.work_time_in_mins || user.byOperator?.workTimeinMins || user.byOperator?.workTimeInMins || user.byOperator?.work_time_in_mins || user.byOperator?.workTimeInMinutes || 0;
      const totalOTMins = user.OTMins || user.byOperator?.OTMins || 0;
      const otMins1_5 = user.OTMins1_5 || user.byOperator?.OTMins1_5 || user.byOperator?.['OTMins1.5'] || user.byOperator?.OTMins15 || user.byOperator?.otMins1_5 || 0;

      let otPercentage = user.OTPercentage || user.otPercentage || user.byOperator?.OTPercentage || user.byOperator?.otPercentage || user.byOperator?.OT_Percentage || user.byOperator?.ot_percentage || '0%';
      if (typeof otPercentage === 'number') {
        otPercentage = Math.ceil(otPercentage) + '%';
      } else if (!otPercentage.toString().includes('%')) {
        otPercentage = otPercentage + '%';
      }

      const dateOfJoining = user.dateOfJoining || user.byOperator?.dateOfJoining || '';
      const formattedDateOfJoining = dateOfJoining ? new Date(dateOfJoining).toLocaleDateString() : '';

      const row = {
        'Name': userName,
        'Desig': userDesignation,
        'User ID': userId,
        'Total Present': totalPresent,
        'Total Absence': totalAbsent,
        '% of Present': presencePercent,
        '% of Absence': absencePercent,
        'Total Work Time in Mins': workTimeinMins,
        'Total OT (in minutes)': totalOTMins,
        'Total OT Mins (1.5 Times)': otMins1_5,
        'OT % on regular': otPercentage,
        'Date Of': formattedDateOfJoining,
        'Total Work (in Hrs)': user.workTimeinHrs || user.workTimeInHrs || user.work_time_in_hrs || 0,
        'Total OT (in Hrs)': user.OTHrs || user.OTHours || user.ot_hrs || 0
      };

      // Add day-wise attendance
      const attendance = user.attendance || user.attendanceData || user.dayWiseAttendance || user.day_wise_attendance || {};
      for (let day = 1; day <= daysInMonth; day++) {
        let status = '';
        if (attendance[day] !== undefined) {
          status = attendance[day];
        } else if (attendance[`day${day}`] !== undefined) {
          status = attendance[`day${day}`];
        } else if (attendance[`Day${day}`] !== undefined) {
          status = attendance[`Day${day}`];
        } else if (attendance[String(day)] !== undefined) {
          status = attendance[String(day)];
        }

        const isPresent = status === 'P' || status === 'p' || status === 'Present' || status === 'present';
        const isAbsent = status === 'A' || status === 'a' || status === 'Absent' || status === 'absent';
        row[`Day ${day}`] = status ? (isPresent ? 'P' : isAbsent ? 'A' : status) : '';
      }

      return row;
    });

    // Combine summary rows and user rows
    const allRows = [...summaryRows, ...userRows];

    // Create worksheet from the data
    const ws = XLSX.utils.json_to_sheet(allRows, { header: headers });

    // Set column widths
    const colWidths = [
      { wch: 20 },  // Name
      { wch: 15 },  // Desig
      { wch: 12 },  // User ID
      { wch: 12 },  // Total Present
      { wch: 12 },  // Total Absence
      { wch: 12 },  // % of Present
      { wch: 12 },  // % of Absence
      { wch: 20 },  // Total Work Time in Mins
      { wch: 18 },  // Total OT (in minutes)
      { wch: 22 },  // Total OT Mins (1.5 Times)
      { wch: 15 },  // OT % on regular
      { wch: 12 },  // Date Of
      { wch: 18 },  // Total Work (in Hrs)
      { wch: 18 },  // Total OT (in Hrs)
      ...Array.from({ length: daysInMonth }, () => ({ wch: 8 })) // Day columns
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'By Operator');

    // Generate filename
    const monthStr = byOperatorMonth.replace('-', '_');
    const fileName = `by_operator_attendance_${monthStr}.xlsx`;

    // Download the file
    XLSX.writeFile(wb, fileName);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError(null);
  };

  return (
    <div className="attendance-report-container">
      <div className="attendance-header">
        <h1>Attendance Report</h1>

        {/* Tabs for Daily, Monthly, Yearly */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="attendance report tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                minHeight: '48px'
              }
            }}
          >
            <Tab
              icon={<CalendarTodayIcon />}
              label="Daily Attendance"
              iconPosition="start"
            />
            <Tab
              icon={<CalendarMonthIcon />}
              label="Monthly Attendance"
              iconPosition="start"
            />
            <Tab
              icon={<CalendarMonthIcon />}
              label="Monthly User"
              iconPosition="start"
            />
            <Tab
              icon={<DateRangeIcon />}
              label="Yearly Attendance"
              iconPosition="start"
            />
            <Tab
              icon={<CalendarMonthIcon />}
              label="Yearly User"
              iconPosition="start"
            />
            <Tab
              icon={<CalendarTodayIcon />}
              label="A/P"
              iconPosition="start"
            />
            <Tab
              icon={<CalendarTodayIcon />}
              label="By Operator"
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Date/Month/Year Selectors and Filters */}
        <div className="date-selector" style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
          {activeTab === 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Date: </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif'
                  }}
                />
              </div>

              {/* Name Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Name: </label>
                <select
                  value={selectedName}
                  onChange={(e) => setSelectedName(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Names</option>
                  {[...new Set(dailyData.map(item => item.employeeName || item.name).filter(Boolean))].sort().map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {/* Designation Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Designation: </label>
                <select
                  value={selectedDesignation}
                  onChange={(e) => setSelectedDesignation(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Designations</option>
                  {[...new Set(dailyData.map(item => item.designation).filter(item => item && item !== '-'))].sort().map(designation => (
                    <option key={designation} value={designation}>{designation}</option>
                  ))}
                </select>
              </div>

              {/* Machine Type Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Machine Type: </label>
                <select
                  value={selectedMachineType}
                  onChange={(e) => setSelectedMachineType(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Machine Types</option>
                  {[...new Set(dailyData.map(item => item.machineType).filter(item => item && item !== '-'))].sort().map(machineType => (
                    <option key={machineType} value={machineType}>{machineType}</option>
                  ))}
                </select>
              </div>

              {/* Present Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Present: </label>
                <select
                  value={selectedPresent}
                  onChange={(e) => setSelectedPresent(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </>
          )}
          {activeTab === 1 && (
            <>
              <label style={{ marginRight: '0.5rem', fontWeight: 500 }}>Month: </label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontFamily: 'Roboto, sans-serif'
                }}
              />
            </>
          )}
          {activeTab === 2 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Month: </label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif'
                  }}
                />
              </div>

              {/* Designation Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Designation: </label>
                <select
                  value={selectedDesignation}
                  onChange={(e) => setSelectedDesignation(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Designations</option>
                  {[...new Set(monthlyUserData.map(item => item.designation).filter(item => item && item !== '-'))].sort().map(designation => (
                    <option key={designation} value={designation}>{designation}</option>
                  ))}
                </select>
              </div>

              {/* Machine Type Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Machine Type: </label>
                <select
                  value={selectedMachineType}
                  onChange={(e) => setSelectedMachineType(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Machine Types</option>
                  {[...new Set(monthlyUserData.map(item => item.machineType).filter(item => item && item !== '-'))].sort().map(machineType => (
                    <option key={machineType} value={machineType}>{machineType}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          {activeTab === 3 && (
            <>
              <label style={{ marginRight: '0.5rem', fontWeight: 500 }}>Year: </label>
              <input
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                min="2020"
                max="2099"
                style={{
                  padding: '0.5rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontFamily: 'Roboto, sans-serif',
                  width: '120px'
                }}
              />
            </>
          )}
          {activeTab === 4 && (
            <>
              <label style={{ marginRight: '0.5rem', fontWeight: 500 }}>Year: </label>
              <input
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                min="2020"
                max="2099"
                style={{
                  padding: '0.5rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontFamily: 'Roboto, sans-serif',
                  width: '120px'
                }}
              />
            </>
          )}
          {activeTab === 5 && (
            <>
              {/* Month filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Month: </label>
                <select
                  value={attendanceGridMonth}
                  onChange={(e) => setAttendanceGridMonth(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '200px'
                  }}
                >
                  {getAllMonths().map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>

              {/* Machine Type filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Machine Type: </label>
                <select
                  value={attendanceGridMachineType}
                  onChange={(e) => setAttendanceGridMachineType(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Machine Types</option>
                  {[...new Set(allAttendanceGridUsers.map(item => item.machineType).filter(Boolean))]
                    .sort()
                    .map(machineType => (
                      <option key={machineType} value={machineType}>{machineType}</option>
                    ))}
                </select>
              </div>

              {/* Designation filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Designation: </label>
                <select
                  value={attendanceGridDesignation}
                  onChange={(e) => setAttendanceGridDesignation(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Designations</option>
                  {[...new Set(allAttendanceGridUsers.map(item => item.designation).filter(Boolean))]
                    .sort()
                    .map(designation => (
                      <option key={designation} value={designation}>{designation}</option>
                    ))}
                </select>
              </div>

              {/* User Search */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Search User: </label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <SearchIcon
                    style={{
                      position: 'absolute',
                      left: '0.75rem',
                      color: '#666',
                      fontSize: '1.2rem'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={attendanceGridSearch}
                    onChange={(e) => setAttendanceGridSearch(e.target.value)}
                    style={{
                      padding: '0.5rem 0.5rem 0.5rem 2.5rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontFamily: 'Roboto, sans-serif',
                      minWidth: '200px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            </>
          )}
          {activeTab === 6 && (
            <>
              {/* Month filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Month: </label>
                <select
                  value={byOperatorMonth}
                  onChange={(e) => {
                    const newMonth = e.target.value;
                    console.log('📅 Month filter changed from', byOperatorMonth, 'to', newMonth);

                    // Cancel any pending API request
                    if (abortController) {
                      console.log('🛑 Cancelling pending API request due to month change...');
                      abortController.abort();
                      setAbortController(null);
                    }

                    // Reset ALL states completely before changing month
                    setIsApiCallInProgress(false);
                    setByOperatorLoading(true); // Show loading immediately
                    setByOperatorError(null); // Clear any previous errors

                    // Clear ALL previous data immediately
                    setByOperatorData([]);
                    setByDateData([]);
                    setByOperatorApiData([]);
                    setAllByOperatorUsers([]);

                    // Update month - this will trigger useEffect which will reload data
                    setByOperatorMonth(newMonth);
                  }}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '200px'
                  }}
                >
                  {getAllMonths().map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>

              {/* Machine Type filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Machine Type: </label>
                <select
                  value={byOperatorMachineType}
                  onChange={(e) => setByOperatorMachineType(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Machine Types</option>
                  {[...new Set(allByOperatorUsers.map(item => item.machineType).filter(Boolean))]
                    .sort()
                    .map(machineType => (
                      <option key={machineType} value={machineType}>{machineType}</option>
                    ))}
                </select>
              </div>

              {/* Designation filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Designation: </label>
                <select
                  value={byOperatorDesignation}
                  onChange={(e) => setByOperatorDesignation(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    minWidth: '150px'
                  }}
                >
                  <option value="all">All Designations</option>
                  {[...new Set(allByOperatorUsers.map(item => item.designation).filter(Boolean))]
                    .sort()
                    .map(designation => (
                      <option key={designation} value={designation}>{designation}</option>
                    ))}
                </select>
              </div>

              {/* User Search */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontWeight: 500 }}>Search User: </label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <SearchIcon
                    style={{
                      position: 'absolute',
                      left: '0.75rem',
                      color: '#666',
                      fontSize: '1.2rem'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search by name or User ID..."
                    value={byOperatorSearch}
                    onChange={(e) => setByOperatorSearch(e.target.value)}
                    style={{
                      padding: '0.5rem 0.5rem 0.5rem 2.5rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontFamily: 'Roboto, sans-serif',
                      minWidth: '200px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Export Excel Button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportByOperatorDataToExcel}
                  style={{
                    backgroundColor: '#1B5E20',
                    color: '#fff',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: 'Roboto, sans-serif',
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor: '#2E7D32'
                    }
                  }}
                >
                  Export Excel
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="spinner" style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Loading attendance data...</p>
        </div>
      ) : error ? (
        <div className="error-message" style={{
          textAlign: 'center',
          padding: '2rem',
          color: '#e74c3c',
          background: 'white',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <WarningIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          {error}
        </div>
      ) : (
        <>
          {/* Daily Attendance Tab */}
          {activeTab === 0 && (() => {
            // Filter daily data based on selected filters
            const filteredDailyData = dailyData.filter(row => {
              const nameMatch = selectedName === 'all' || (row.employeeName || row.name) === selectedName;
              const designationMatch = selectedDesignation === 'all' || row.designation === selectedDesignation;
              const machineTypeMatch = selectedMachineType === 'all' || row.machineType === selectedMachineType;
              // Filter by present status
              let presentMatch = true;
              if (selectedPresent === 'yes') {
                presentMatch = row.present === 1;
              } else if (selectedPresent === 'no') {
                presentMatch = row.present === 0 || row.absent === 1;
              }
              return nameMatch && designationMatch && machineTypeMatch && presentMatch;
            });

            return (
              <div className="daily-attendance-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div className="section-title">Daily Attendance - {formatDate(selectedDate)}</div>
                  <Button
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    onClick={exportDailyDataToExcel}
                    disabled={filteredDailyData.length === 0}
                    sx={{
                      backgroundColor: '#667eea',
                      '&:hover': {
                        backgroundColor: '#5568d3'
                      },
                      '&:disabled': {
                        backgroundColor: '#cccccc'
                      },
                      textTransform: 'none',
                      fontWeight: 500,
                      padding: '0.5rem 1rem'
                    }}
                  >
                    Export Excel
                  </Button>
                </div>
                <div className="attendance-table-container">
                  <div className="table-scroll-wrapper">
                    <table className="attendance-table">
                      <thead>
                        <tr>
                          <th className="row-number-col">#</th>
                          <th className="name-col">Name</th>
                          <th className="name-col">Username</th>
                          <th className="name-col">Machine Type</th>
                          <th className="name-col">Designation</th>
                          <th className="present-col">Present</th>
                          <th className="regular-hours-col">Work Time (Hrs)</th>
                          <th className="ot-hours-col">Over Time (Hrs)</th>
                          <th className="name-col">Over Time 1.5 Times (Mins)</th>
                          <th className="name-col">Per Hour Rate (Rs.)</th>
                          <th className="name-col">Over Time Pay (Rs.)</th>
                          <th className="total-col">Machine Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDailyData.length > 0 ? (
                          <>
                            {filteredDailyData.map((row, index) => (
                              <tr key={index}>
                                <td className="row-number">{index + 1}</td>
                                <td className="name-cell">{row.employeeName || row.name || '-'}</td>
                                <td className="name-cell">{row.userName || '-'}</td>
                                <td className="name-cell">{row.machineType || '-'}</td>
                                <td className="name-cell">{row.designation || '-'}</td>
                                <td className="present-cell">{row.present === 1 ? 'Yes' : 'No'}</td>
                                <td className="regular-hours-cell">{formatHoursWithColon(row.workTime || row.regularHours || 0)}</td>
                                <td className="ot-hours-cell">{formatHoursWithColon(row.overTime || row.otHours || 0)}</td>
                                <td className="name-cell">{row.overTime1_5 || 0}</td>
                                <td className="name-cell">{row.perHourRate || 0}</td>
                                <td className="name-cell">{row.overTimePa || 0}</td>
                                <td className="total-cell">{formatHoursWithColon(row.machineTime || '00:00')}</td>
                              </tr>
                            ))}
                            <tr className="summary-row">
                              <td className="row-number"></td>
                              <td className="summary-label"><strong>Total</strong></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td className="summary-value"><strong>
                                {selectedPresent === 'all'
                                  ? dailyData.filter(row => {
                                    const nameMatch = selectedName === 'all' || (row.employeeName || row.name) === selectedName;
                                    const designationMatch = selectedDesignation === 'all' || row.designation === selectedDesignation;
                                    const machineTypeMatch = selectedMachineType === 'all' || row.machineType === selectedMachineType;
                                    return nameMatch && designationMatch && machineTypeMatch;
                                  }).length
                                  : filteredDailyData.length}
                              </strong></td>
                              <td className="summary-value"><strong>{formatHoursWithColon(filteredDailyData.reduce((sum, row) => sum + convertWorkTimeToHours(row.workTime || row.regularHours || '00:00'), 0))}</strong></td>
                              <td className="summary-value"><strong>{formatHoursWithColon(filteredDailyData.reduce((sum, row) => sum + convertWorkTimeToHours(row.overTime || row.otHours || '00:00'), 0))}</strong></td>
                              <td className="summary-value"><strong>{filteredDailyData.reduce((sum, row) => sum + (parseFloat(row.overTime1_5 || 0)), 0)}</strong></td>
                              <td className="summary-value"><strong>{Math.ceil(filteredDailyData.reduce((sum, row) => sum + (parseFloat(row.perHourRate || 0)), 0))}</strong></td>
                              <td className="summary-value"><strong>{Math.ceil(filteredDailyData.reduce((sum, row) => sum + (parseFloat(row.overTimePa || 0)), 0))}</strong></td>
                              <td className="summary-value"><strong>{formatHoursWithColon(filteredDailyData.reduce((sum, row) => sum + convertWorkTimeToHours(row.machineTime || '00:00'), 0))}</strong></td>
                            </tr>
                          </>
                        ) : (
                          <tr>
                            <td colSpan="12" style={{ textAlign: 'center', padding: '2rem' }}>
                              No attendance data available {selectedName !== 'all' || selectedDesignation !== 'all' || selectedMachineType !== 'all' || selectedPresent !== 'all' ? 'for the selected filters' : 'for the selected date'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Monthly Attendance Tab */}
          {activeTab === 1 && (
            <div className="monthly-attendance-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div className="section-title">Monthly Attendance Summary - {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportMonthlyDataToExcel}
                  disabled={monthlyData.length === 0}
                  sx={{
                    backgroundColor: '#667eea',
                    '&:hover': {
                      backgroundColor: '#5568d3'
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc'
                    },
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '0.5rem 1rem'
                  }}
                >
                  Export Excel
                </Button>
              </div>
              <div className="monthly-table-container">
                <div className="table-scroll-wrapper">
                  <table className="attendance-table monthly-table">
                    <thead>
                      <tr>
                        <th className="date-col">Date</th>
                        <th className="present-col">Present Count</th>
                        <th className="abset-col">Absent Count</th>
                        <th className="total-regular-col">Total Work Time</th>
                        <th className="total-ot-col">Total Over Time</th>
                        <th className="total-col">Total Machine Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.length > 0 ? (
                        monthlyData.map((row, index) => {
                          const date = row.date || '';
                          const presentCount = parseInt(row.presentCount || row.present_count || 0);
                          const absentCount = parseInt(row.absentCount || row.absent_count || 0);
                          const totalWorkTime = row.totalWorkTime || row.total_work_time || row.workTime || '00:00';
                          const totalOverTime = row.totalOverTime || row.total_over_time || row.overTime || '00:00';
                          const totalMachineTime = row.totalMachineTime || row.total_machine_time || row.machineTime || '00:00';

                          // Debug log for each row
                          if (index === 0) {
                            console.log('Monthly row data:', { date, presentCount, absentCount, totalWorkTime, totalOverTime, totalMachineTime });
                          }

                          return (
                            <tr key={index}>
                              <td className="date-cell">{date ? formatDate(date) : '-'}</td>
                              <td className="present-cell">{presentCount}</td>
                              <td className="abset-cell">{absentCount}</td>
                              <td className="total-regular-cell">{totalWorkTime}</td>
                              <td className="total-ot-cell">{totalOverTime}</td>
                              <td className="total-cell">{totalMachineTime}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                            No attendance data available for the selected month.
                            <br />
                            <small style={{ color: '#666' }}>Monthly data length: {monthlyData.length}</small>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Monthly User Attendance Tab */}
          {activeTab === 2 && (() => {
            // Filter monthly user data based on selected filters
            const filteredMonthlyUserData = monthlyUserData.filter(row => {
              const designationMatch = selectedDesignation === 'all' || row.designation === selectedDesignation;
              const machineTypeMatch = selectedMachineType === 'all' || row.machineType === selectedMachineType;
              return designationMatch && machineTypeMatch;
            });

            return (
              <div className="monthly-user-attendance-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div className="section-title">Monthly User Attendance - {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                  <Button
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    onClick={exportMonthlyUserDataToExcel}
                    disabled={filteredMonthlyUserData.length === 0}
                    sx={{
                      backgroundColor: '#667eea',
                      '&:hover': {
                        backgroundColor: '#5568d3'
                      },
                      '&:disabled': {
                        backgroundColor: '#cccccc'
                      },
                      textTransform: 'none',
                      fontWeight: 500,
                      padding: '0.5rem 1rem'
                    }}
                  >
                    Export Excel
                  </Button>
                </div>
                <div className="attendance-table-container">
                  <div className="table-scroll-wrapper">
                    <table className="attendance-table">
                      <thead>
                        <tr>
                          <th className="row-number-col">#</th>
                          <th className="name-col">Employee Name</th>
                          <th className="name-col">Username</th>
                          <th className="name-col">Designation</th>
                          <th className="name-col">Machine Type</th>
                          <th className="present-col">Total Present (Days)</th>
                          <th className="abset-col">Total Absent (Days)</th>
                          <th className="regular-hours-col">Total Absent Days(HH:MM)</th>
                          <th className="regular-hours-col">Total Absent Days (Minutes)</th>
                          <th className="regular-hours-col">Total Work Time (HH:MM)</th>
                          <th className="ot-hours-col">Total Over Time (HH:MM)</th>
                          <th className="ot-hours-col">Total OT (Minutes)</th>
                          <th className="ot-hours-col">Adjusted OT (HH : MM)</th>
                          <th className="name-col">OT 1.5 Times((Minutes)</th>
                          <th className="name-col">Per Hr Pay (Rupees)</th>
                          <th className="name-col">OT Pay (Rupees)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMonthlyUserData.length > 0 ? (
                          filteredMonthlyUserData.map((row, index) => (
                            <tr key={index}>
                              <td className="row-number">{index + 1}</td>
                              <td className="name-cell">{row.employeeName || '-'}</td>
                              <td className="name-cell">{row.userName || '-'}</td>
                              <td className="name-cell">{row.designation || '-'}</td>
                              <td className="name-cell">{row.machineType || '-'}</td>
                              <td className="present-cell">{row.totalPresent || 0}</td>
                              <td className="abset-cell">{row.totalAbsent || 0}</td>
                              <td className="regular-hours-cell">{row.totalAbsentHrsMins || '00:00'}</td>
                              <td className="regular-hours-cell">{row.totalAbsentDaysInMins || 0}</td>
                              <td className="regular-hours-cell">{row.totalWorkTime || '00:00'}</td>
                              <td className="ot-hours-cell">{row.totalOverTime || '00:00'}</td>
                              <td className="ot-hours-cell">{row.totalOverMinutes || 0}</td>
                              <td className="ot-hours-cell">{row.adjustedOT || '00:00'}</td>
                              <td className="name-cell">{row.overTime1_5 || 0}</td>
                              <td className="name-cell">{row.perHourRate || 0}</td>
                              <td className="name-cell">{row.otPay || 0}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="16" style={{ textAlign: 'center', padding: '2rem' }}>
                              No attendance data available {selectedDesignation !== 'all' || selectedMachineType !== 'all' ? 'for the selected filters' : 'for the selected month'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Yearly Attendance Tab */}
          {activeTab === 3 && (
            <div className="yearly-attendance-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div className="section-title">Yearly Attendance Summary - {selectedYear}</div>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportYearlyDataToExcel}
                  disabled={yearlyData.length === 0}
                  sx={{
                    backgroundColor: '#667eea',
                    '&:hover': {
                      backgroundColor: '#5568d3'
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc'
                    },
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '0.5rem 1rem'
                  }}
                >
                  Export Excel
                </Button>
              </div>
              <div className="yearly-table-container">
                <div className="table-scroll-wrapper">
                  <table className="attendance-table yearly-table">
                    <thead>
                      <tr>
                        <th className="month-col">Month</th>
                        <th className="present-col">Present Count</th>
                        <th className="abset-col">Absent Count</th>
                        <th className="total-regular-col">Total Work Time</th>
                        <th className="total-ot-col">Total Over Time</th>
                        <th className="total-col">Total Machine Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearlyData.length > 0 ? (
                        yearlyData.map((row, index) => {
                          const month = row.month || '-';
                          const presentCount = parseInt(row.presentCount || 0);
                          const absentCount = parseInt(row.absentCount || 0);
                          const totalWorkTime = row.totalWorkTime || '00:00';
                          const totalOverTime = row.totalOverTime || '00:00';
                          const totalMachineTime = row.totalMachineTime || '00:00';

                          return (
                            <tr key={index}>
                              <td className="month-cell">{month}</td>
                              <td className="present-cell">{presentCount}</td>
                              <td className="abset-cell">{absentCount}</td>
                              <td className="total-regular-cell">{totalWorkTime}</td>
                              <td className="total-ot-cell">{totalOverTime}</td>
                              <td className="total-cell">{totalMachineTime}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                            No attendance data available for the selected year
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Yearly User Attendance Tab */}
          {activeTab === 4 && (
            <div className="yearly-user-attendance-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div className="section-title">Jan - Dec {selectedYear}</div>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportYearlyUserDataToExcel}
                  disabled={yearlyUserData.length === 0}
                  sx={{
                    backgroundColor: '#667eea',
                    '&:hover': {
                      backgroundColor: '#5568d3'
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc'
                    },
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '0.5rem 1rem'
                  }}
                >
                  Export Excel
                </Button>
              </div>
              <div className="attendance-table-container">
                <div className="table-scroll-wrapper">
                  <table className="attendance-table">
                    <thead>
                      <tr>
                        <th className="name-col">Name</th>
                        <th className="month-col">Jan</th>
                        <th className="month-col">Feb</th>
                        <th className="month-col">Mar</th>
                        <th className="month-col">Apr</th>
                        <th className="month-col">May</th>
                        <th className="month-col">June</th>
                        <th className="month-col">Jul</th>
                        <th className="month-col">Aug</th>
                        <th className="month-col">Sep</th>
                        <th className="month-col">Oct</th>
                        <th className="month-col">Nov</th>
                        <th className="month-col">Dec</th>
                        <th className="total-col">Total Hrs</th>
                        <th className="total-col">Total ET</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearlyUserData.length > 0 ? (
                        <>
                          {yearlyUserData.map((row, index) => (
                            <tr key={index}>
                              <td className="name-cell">{row.employeeName || '-'}</td>
                              <td className="month-cell">{row.jan || '00:00'}</td>
                              <td className="month-cell">{row.feb || '00:00'}</td>
                              <td className="month-cell">{row.mar || '00:00'}</td>
                              <td className="month-cell">{row.apr || '00:00'}</td>
                              <td className="month-cell">{row.may || '00:00'}</td>
                              <td className="month-cell">{row.june || '00:00'}</td>
                              <td className="month-cell">{row.jul || '00:00'}</td>
                              <td className="month-cell">{row.aug || '00:00'}</td>
                              <td className="month-cell">{row.sep || '00:00'}</td>
                              <td className="month-cell">{row.oct || '00:00'}</td>
                              <td className="month-cell">{row.nov || '00:00'}</td>
                              <td className="month-cell">{row.dec || '00:00'}</td>
                              <td className="total-cell">{row.totalHrs || '00:00'}</td>
                              <td className="total-cell">{Math.round(row.totalET || 0)}</td>
                            </tr>
                          ))}
                          <tr className="summary-row">
                            <td className="summary-label"><strong>Total</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.jan || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.feb || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.mar || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.apr || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.may || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.june || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.jul || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.aug || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.sep || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.oct || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.nov || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.dec || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{(() => {
                              const total = yearlyUserData.reduce((sum, row) => sum + convertWorkTimeToHours(row.totalHrs || '00:00'), 0);
                              const hours = Math.floor(total);
                              const minutes = Math.round((total - hours) * 60);
                              return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                            })()}</strong></td>
                            <td className="summary-value"><strong>{Math.round(yearlyUserData.reduce((sum, row) => sum + (parseFloat(row.totalET) || 0), 0))}</strong></td>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <td colSpan="15" style={{ textAlign: 'center', padding: '2rem' }}>
                            No attendance data available for the selected year
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ne tab - Attendance Grid (P/A per user per day) */}
          {activeTab === 5 && (
            <div className="attendance-grid-section">
              {attendanceGridError && (
                <div style={{
                  padding: '0.75rem 1rem',
                  marginBottom: '1rem',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  color: '#856404'
                }}>
                  <WarningIcon style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  {attendanceGridError}
                </div>
              )}

              {attendanceGridLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <Typography>Loading attendance grid data...</Typography>
                </div>
              ) : (
                <div className="attendance-table-container">
                  <div className="table-scroll-wrapper">
                    <table className="attendance-grid-table">
                      <thead>
                        <tr>
                          <th className="attendance-grid-name-col">Name</th>
                          {Array.from({ length: getDaysInMonth(attendanceGridMonth) }, (_, i) => i + 1).map(day => (
                            <th key={day} className="attendance-grid-day-col">{day}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceGridData.length > 0 ? (
                          attendanceGridData.map((user, userIndex) => (
                            <tr key={userIndex}>
                              <td className="attendance-grid-name-cell">{user.name}</td>
                              {Array.from({ length: getDaysInMonth(attendanceGridMonth) }, (_, i) => {
                                const day = i + 1;
                                const status = user.attendance[day] || '';
                                const isPresent = status === 'P';
                                const isAbsent = status === 'A';
                                return (
                                  <td
                                    key={day}
                                    className={`attendance-grid-day-cell ${isPresent ? 'attendance-present' : isAbsent ? 'attendance-absent' : ''
                                      }`}
                                  >
                                    {status}
                                  </td>
                                );
                              })}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={getDaysInMonth(attendanceGridMonth) + 1}
                              style={{ textAlign: 'center', padding: '2rem' }}
                            >
                              No attendance data available for the selected filters
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Area chart below grid - Present vs Absent per day */}
              {attendanceGridData.length > 0 && (() => {
                const daysInMonth = getDaysInMonth(attendanceGridMonth);
                const chartData = [];

                for (let day = 1; day <= daysInMonth; day++) {
                  let presentCount = 0;
                  let absentCount = 0;

                  attendanceGridData.forEach(user => {
                    const status = user.attendance[day];
                    if (status === 'P') presentCount++;
                    else if (status === 'A') absentCount++;
                  });

                  chartData.push({
                    day: day.toString(),
                    Present: presentCount,
                    Absent: absentCount
                  });
                }

                return (
                  <div className="attendance-graph-container" style={{ marginTop: '2rem' }}>
                    <div className="attendance-table-container">
                      <h3 style={{ marginBottom: '1.5rem', color: '#202124', fontSize: '1.25rem', fontWeight: 600 }}>
                        Attendance Area Chart - Present vs Absent
                      </h3>
                      <ResponsiveContainer width="100%" height={400}>
                        <AreaChart
                          data={chartData}
                          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#C8E6C9" stopOpacity={0.9} />
                              <stop offset="95%" stopColor="#C8E6C9" stopOpacity={0.2} />
                            </linearGradient>
                            <linearGradient id="colorAbsent" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#FFCDD2" stopOpacity={0.9} />
                              <stop offset="95%" stopColor="#FFCDD2" stopOpacity={0.2} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis
                            dataKey="day"
                            label={{ value: 'Day', position: 'insideBottom', offset: -5 }}
                            stroke="#666"
                            tick={{ fill: '#666' }}
                          />
                          <YAxis
                            label={{ value: 'Number of Employees', angle: -90, position: 'insideLeft' }}
                            stroke="#666"
                            tick={{ fill: '#666' }}
                            domain={[0, 'dataMax + 2']}
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
                            formatter={(value, name) => [value, name]}
                          />
                          <Legend
                            verticalAlign="top"
                            align="right"
                            wrapperStyle={{ paddingBottom: '20px' }}
                          />
                          <Area
                            type="monotone"
                            dataKey="Present"
                            stackId="1"
                            stroke="#1B5E20"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorPresent)"
                          />
                          <Area
                            type="monotone"
                            dataKey="Absent"
                            stackId="1"
                            stroke="#B71C1C"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorAbsent)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* By Operator tab */}
          {activeTab === 6 && (
            <div className="by-operator-section">
              {byOperatorError && (
                <div style={{
                  padding: '0.75rem 1rem',
                  marginBottom: '1rem',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  color: '#856404'
                }}>
                  <WarningIcon style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  {byOperatorError}
                </div>
              )}

              {byOperatorLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <Typography>Loading By Operator data...</Typography>
                </div>
              ) : (
                <>
                  <div className="attendance-table-container">
                    {/* Top horizontal scrollbar for By Operator table */}
                    <div
                      className="top-scrollbar-container"
                      ref={topScrollbarRef}
                      onScroll={handleTopScroll}
                      style={{ overflowX: 'auto', overflowY: 'hidden', height: '12px', marginBottom: '2px' }}
                    >
                      <div style={{ width: `${tableWidth}px`, height: '1px' }}></div>
                    </div>

                    <div className="table-scroll-wrapper" ref={tableWrapperRef} onScroll={handleTableScroll}>
                      <table className="by-operator-main-table" ref={tableRef}>
                        <thead>
                          {/* Top header row */}
                          <tr>
                            <th className="name-header-col"></th>
                            <th colSpan={3} className="summary-header-col">Name</th>
                            <th colSpan={11} className="by-operator-header">By Operator</th>
                            <th colSpan={getDaysInMonth(byOperatorMonth)} className="date-header">
                              Date
                            </th>
                          </tr>
                          {/* Second header row with detailed labels */}
                          <tr>
                            <th className="name-header-col"></th>
                            <th className="summary-header-col">Name</th>
                            <th className="summary-header-col">Desig</th>
                            <th className="summary-header-col">User ID</th>
                            <th className="by-operator-col-header">Total Present</th>
                            <th className="by-operator-col-header">Total Absence</th>
                            <th className="by-operator-col-header">% of Present</th>
                            <th className="by-operator-col-header">% of Absence</th>
                            <th className="by-operator-col-header">Total Work Time in Mins</th>
                            <th className="by-operator-col-header">Total OT (in minutes)</th>
                            <th className="by-operator-col-header">Total OT Mins (1.5 Times)</th>
                            <th className="by-operator-col-header">OT % on regular</th>
                            <th className="by-operator-col-header">Date Of</th>
                            <th className="by-operator-col-header">Total work (in Hrs)</th>
                            <th className="by-operator-col-header">Total OT (in Hrs)</th>
                            {Array.from({ length: getDaysInMonth(byOperatorMonth) }, (_, i) => i + 1).map(day => (
                              <th key={day} className="day-header">{day}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Summary rows - Day-wise totals from byDate API data */}
                          {/* Total Present row */}
                          <tr>
                            <td className="summary-row-label">Total Present</td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            {Array.from({ length: getDaysInMonth(byOperatorMonth) }, (_, i) => {
                              const day = i + 1;
                              // Use byDate data if available, otherwise calculate from byOperatorData
                              const dateItem = byDateData.find(item => item.day === day);
                              const value = dateItem ? dateItem.totalPresent :
                                byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day] === 'P') return sum + 1;
                                  return sum;
                                }, 0);
                              return (
                                <td key={day} className="summary-day-value">{value}</td>
                              );
                            })}
                          </tr>
                          {/* Total Absent row */}
                          <tr>
                            <td className="summary-row-label">Total Absence</td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            {Array.from({ length: getDaysInMonth(byOperatorMonth) }, (_, i) => {
                              const day = i + 1;
                              const dateItem = byDateData.find(item => item.day === day);
                              const value = dateItem ? dateItem.totalAbsent :
                                byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day] === 'A') return sum + 1;
                                  return sum;
                                }, 0);
                              return (
                                <td key={day} className="summary-day-value">{value}</td>
                              );
                            })}
                          </tr>
                          {/* %of Presence row */}
                          <tr>
                            <td className="summary-row-label">% of Present</td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            {Array.from({ length: getDaysInMonth(byOperatorMonth) }, (_, i) => {
                              const day = i + 1;
                              const dateItem = byDateData.find(item => item.day === day);
                              let percent = '0';
                              if (dateItem) {
                                // percentageOfPresence is a number, convert to string with %
                                percent = typeof dateItem.percentageOfPresence === 'number'
                                  ? String(Math.ceil(dateItem.percentageOfPresence))
                                  : String(Math.ceil(parseFloat((dateItem.percentageOfPresence || '0').toString().replace('%', '')) || 0));
                              } else {
                                const dayPresent = byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day] === 'P') return sum + 1;
                                  return sum;
                                }, 0);
                                const dayAbsent = byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day] === 'A') return sum + 1;
                                  return sum;
                                }, 0);
                                const total = dayPresent + dayAbsent;
                                percent = total > 0 ? String(Math.ceil((dayPresent / total) * 100)) : '0';
                              }
                              return (
                                <td key={day} className="summary-day-value">{percent}%</td>
                              );
                            })}
                          </tr>
                          {/* %of absence row */}
                          <tr>
                            <td className="summary-row-label">% of Absence</td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            {Array.from({ length: getDaysInMonth(byOperatorMonth) }, (_, i) => {
                              const day = i + 1;
                              const dateItem = byDateData.find(item => item.day === day);
                              let percent = '0';
                              if (dateItem) {
                                // percentageOfAbsence is a number, convert to string with %
                                percent = typeof dateItem.percentageOfAbsence === 'number'
                                  ? String(Math.ceil(dateItem.percentageOfAbsence))
                                  : String(Math.ceil(parseFloat((dateItem.percentageOfAbsence || '0').toString().replace('%', '')) || 0));
                              } else {
                                const dayPresent = byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day] === 'P') return sum + 1;
                                  return sum;
                                }, 0);
                                const dayAbsent = byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day] === 'A') return sum + 1;
                                  return sum;
                                }, 0);
                                const total = dayPresent + dayAbsent;
                                percent = total > 0 ? String(Math.ceil((dayAbsent / total) * 100)) : '0';
                              }
                              return (
                                <td key={day} className="summary-day-value">{percent}%</td>
                              );
                            })}
                          </tr>
                          {/* Total OT Mins row */}
                          <tr>
                            <td className="summary-row-label">Total OT Mins</td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            {Array.from({ length: getDaysInMonth(byOperatorMonth) }, (_, i) => {
                              const day = i + 1;
                              const dateItem = byDateData.find(item => item.day === day);
                              const value = dateItem ? dateItem.OTMins :
                                byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day]) {
                                    return sum + Math.floor((user.OTMins || 0) / getDaysInMonth(byOperatorMonth));
                                  }
                                  return sum;
                                }, 0);
                              return (
                                <td key={day} className="summary-day-value">{value || ''}</td>
                              );
                            })}
                          </tr>
                          {/* OT % row */}
                          <tr>
                            <td className="summary-row-label">OT %</td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            {Array.from({ length: getDaysInMonth(byOperatorMonth) }, (_, i) => {
                              const day = i + 1;
                              const dateItem = byDateData.find(item => item.day === day);
                              let percent = '0';
                              if (dateItem) {
                                // OTPercentage is a number, convert to string with %
                                percent = typeof dateItem.OTPercentage === 'number'
                                  ? String(Math.ceil(dateItem.OTPercentage))
                                  : String(Math.ceil(parseFloat((dateItem.OTPercentage || '0').toString().replace('%', '')) || 0));
                              } else {
                                const dayOTMins = byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day]) {
                                    return sum + Math.floor((user.OTMins || 0) / getDaysInMonth(byOperatorMonth));
                                  }
                                  return sum;
                                }, 0);
                                const totalWorkMins = byOperatorData.length * 8 * 60;
                                percent = totalWorkMins > 0 ? String(Math.ceil((dayOTMins / totalWorkMins) * 100)) : '0';
                              }
                              return (
                                <td key={day} className="summary-day-value">{percent}%</td>
                              );
                            })}
                          </tr>
                          {/* Total work (in Hrs) row */}
                          <tr>
                            <td className="summary-row-label">Total work (in Hrs)</td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            {Array.from({ length: getDaysInMonth(byOperatorMonth) }, (_, i) => {
                              const day = i + 1;
                              const dateItem = byDateData.find(item => item.day === day);
                              const value = dateItem ? (dateItem.workTimeinHrs || dateItem.workTimeInHrs || dateItem.work_time_in_hrs || dateItem.workTimeInHours || 0) :
                                byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day]) {
                                    return sum + (user.workTimeinHrs || user.workTimeInHrs || user.work_time_in_hrs || 0);
                                  }
                                  return sum;
                                }, 0);
                              return (
                                <td key={day} className="summary-day-value">{value || ''}</td>
                              );
                            })}
                          </tr>
                          {/* Total OT (in Hrs) row */}
                          <tr>
                            <td className="summary-row-label">Total OT (in Hrs)</td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            <td className="summary-by-operator-cell"></td>
                            {Array.from({ length: getDaysInMonth(byOperatorMonth) }, (_, i) => {
                              const day = i + 1;
                              const dateItem = byDateData.find(item => item.day === day);
                              const value = dateItem ? (dateItem.OTHrs || dateItem.OTHours || dateItem.ot_hrs || dateItem.otHours || 0) :
                                byOperatorData.reduce((sum, user) => {
                                  if (user.attendance && user.attendance[day]) {
                                    return sum + (user.OTHrs || user.OTHours || user.ot_hrs || 0);
                                  }
                                  return sum;
                                }, 0);
                              return (
                                <td key={day} className="summary-day-value">{value || ''}</td>
                              );
                            })}
                          </tr>
                          {/* User-wise rows (all users) */}
                          {byOperatorData.length > 0 ? (
                            byOperatorData.map((user, index) => {
                              const daysInMonth = getDaysInMonth(byOperatorMonth);
                              // Map API fields - API structure: user.name, user.userName, user.employeeId, user.designation
                              // byOperator data is already flattened to top level in loadByOperatorDataFromAPI
                              const userName = user.name || '-';
                              const userDesignation = user.designation || '-';
                              const userId = user.userName || user.employeeId || '-';
                              const totalPresent = user.totalPresent || user.byOperator?.totalPresent || 0;
                              const totalAbsent = user.totalAbsent || user.byOperator?.totalAbsent || 0;
                              // percentageOfPresence comes as string like "77.78%" or number
                              let presencePercent = user.percentageOfPresence || user.byOperator?.percentageOfPresence || '0%';
                              if (typeof presencePercent === 'number') {
                                presencePercent = Math.ceil(presencePercent) + '%';
                              } else if (!presencePercent.toString().includes('%')) {
                                presencePercent = presencePercent + '%';
                              }
                              let absencePercent = user.percentageOfAbsence || user.byOperator?.percentageOfAbsence || '0%';
                              if (typeof absencePercent === 'number') {
                                absencePercent = Math.ceil(absencePercent) + '%';
                              } else if (!absencePercent.toString().includes('%')) {
                                absencePercent = absencePercent + '%';
                              }
                              const workTimeinMins = user.workTimeinMins || user.workTimeInMins || user.work_time_in_mins || user.byOperator?.workTimeinMins || user.byOperator?.workTimeInMins || user.byOperator?.work_time_in_mins || user.byOperator?.workTimeInMinutes || 0;
                              const workTimeinHrs = user.workTimeinHrs || user.workTimeInHrs || user.work_time_in_hrs || user.byOperator?.workTimeinHrs || user.byOperator?.workTimeInHrs || user.byOperator?.work_time_in_hrs || user.byOperator?.workTimeInHours || 0;
                              const totalOTMins = user.OTMins || user.byOperator?.OTMins || 0;
                              const totalOTHrs = user.OTHrs || user.OTHours || user.ot_hrs || user.byOperator?.OTHrs || user.byOperator?.OTHours || user.byOperator?.ot_hrs || 0;
                              const otMins1_5 = user.OTMins1_5 || user.byOperator?.OTMins1_5 || user.byOperator?.['OTMins1.5'] || user.byOperator?.OTMins15 || user.byOperator?.otMins1_5 || 0;
                              let otPercentage = user.OTPercentage || user.otPercentage || user.byOperator?.OTPercentage || user.byOperator?.otPercentage || user.byOperator?.OT_Percentage || user.byOperator?.ot_percentage || '0%';
                              // Ensure OTPercentage has % sign
                              if (typeof otPercentage === 'number') {
                                otPercentage = Math.ceil(otPercentage) + '%';
                              } else if (!otPercentage.toString().includes('%')) {
                                otPercentage = otPercentage + '%';
                              }
                              const perHr = user.perHourPay || user.byOperator?.perHourPay || '0';
                              const otPay = user.OTPay || user.byOperator?.OTPay || 0;
                              const dateOfJoining = user.dateOfJoining || user.byOperator?.dateOfJoining || '';

                              // Log first user for debugging
                              if (index === 0) {
                                console.log('👤 First user data:', user);
                                console.log('📋 Mapped values:', {
                                  userName,
                                  userDesignation,
                                  userId,
                                  totalPresent,
                                  totalAbsent,
                                  presencePercent,
                                  absencePercent,
                                  workTimeinMins,
                                  totalOTMins,
                                  otMins1_5,
                                  otPercentage,
                                  perHr,
                                  otPay,
                                  dateOfJoining,
                                  attendance: user.attendance
                                });
                              }

                              return (
                                <tr key={index}>
                                  <td className="user-name-cell"></td>
                                  <td className="user-name-cell">{userName}</td>
                                  <td className="user-name-cell">{userDesignation}</td>
                                  <td className="user-name-cell">{userId}</td>
                                  <td className="by-operator-data-cell">{totalPresent}</td>
                                  <td className="by-operator-data-cell">{totalAbsent}</td>
                                  <td className="by-operator-data-cell">{presencePercent}</td>
                                  <td className="by-operator-data-cell">{absencePercent}</td>
                                  <td className="by-operator-data-cell">{workTimeinMins}</td>
                                  <td className="by-operator-data-cell">{totalOTMins}</td>
                                  <td className="by-operator-data-cell">{otMins1_5}</td>
                                  <td className="by-operator-data-cell">{otPercentage}</td>
                                  <td className="by-operator-data-cell">
                                    {dateOfJoining ? new Date(dateOfJoining).toLocaleDateString() : ''}
                                  </td>
                                  <td className="by-operator-data-cell">{workTimeinHrs}</td>
                                  <td className="by-operator-data-cell">{totalOTHrs}</td>
                                  {Array.from({ length: daysInMonth }, (_, i) => {
                                    const day = i + 1;
                                    // Support multiple attendance field formats
                                    const attendance = user.attendance || user.attendanceData || user.dayWiseAttendance || user.day_wise_attendance || {};

                                    // Try different day key formats
                                    let status = '';
                                    if (attendance[day] !== undefined) {
                                      status = attendance[day];
                                    } else if (attendance[`day${day}`] !== undefined) {
                                      status = attendance[`day${day}`];
                                    } else if (attendance[`Day${day}`] !== undefined) {
                                      status = attendance[`Day${day}`];
                                    } else if (attendance[`d${day}`] !== undefined) {
                                      status = attendance[`d${day}`];
                                    } else if (attendance[String(day)] !== undefined) {
                                      status = attendance[String(day)];
                                    } else if (Array.isArray(attendance) && attendance[day - 1] !== undefined) {
                                      status = attendance[day - 1];
                                    }

                                    const isPresent = status === 'P' || status === 'p' || status === 'Present' || status === 'present';
                                    const isAbsent = status === 'A' || status === 'a' || status === 'Absent' || status === 'absent';

                                    return (
                                      <td
                                        key={day}
                                        className={`by-operator-day-cell ${isPresent ? 'attendance-present' : isAbsent ? 'attendance-absent' : ''
                                          }`}
                                      >
                                        {status ? (isPresent ? 'P' : isAbsent ? 'A' : status) : ''}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan={getDaysInMonth(byOperatorMonth) + 12}
                                style={{ textAlign: 'center', padding: '2rem' }}
                              >
                                No data available for the selected filters
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* A/P Graph at the bottom */}
                  {byOperatorData.length > 0 && (() => {
                    const daysInMonth = getDaysInMonth(byOperatorMonth);
                    const chartData = [];

                    for (let day = 1; day <= daysInMonth; day++) {
                      let presentCount = 0;
                      let absentCount = 0;

                      byOperatorData.forEach(user => {
                        const attendance = user.attendance || user.attendanceData || {};
                        const status = attendance[day] || attendance[`day${day}`] || attendance[`Day${day}`] || '';
                        if (status === 'P' || status === 'p') presentCount++;
                        else if (status === 'A' || status === 'a') absentCount++;
                      });

                      chartData.push({
                        day: day.toString(),
                        Present: presentCount,
                        Absent: absentCount
                      });
                    }

                    return (
                      <div className="attendance-graph-container" style={{ marginTop: '2rem' }}>
                        <div className="attendance-table-container">
                          <h3 style={{ marginBottom: '1.5rem', color: '#202124', fontSize: '1.25rem', fontWeight: 600 }}>
                            Attendance Area Chart - Present vs Absent
                          </h3>
                          <ResponsiveContainer width="100%" height={400}>
                            <AreaChart
                              data={chartData}
                              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                            >
                              <defs>
                                <linearGradient id="colorPresentByOp" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#C8E6C9" stopOpacity={0.9} />
                                  <stop offset="95%" stopColor="#C8E6C9" stopOpacity={0.2} />
                                </linearGradient>
                                <linearGradient id="colorAbsentByOp" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#FFCDD2" stopOpacity={0.9} />
                                  <stop offset="95%" stopColor="#FFCDD2" stopOpacity={0.2} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis
                                dataKey="day"
                                label={{ value: 'Day', position: 'insideBottom', offset: -5 }}
                                stroke="#666"
                                tick={{ fill: '#666' }}
                              />
                              <YAxis
                                label={{ value: 'Number of Employees', angle: -90, position: 'insideLeft' }}
                                stroke="#666"
                                tick={{ fill: '#666' }}
                                domain={[0, 'dataMax + 2']}
                                allowDecimals={false}
                              />
                              <Tooltip
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
                                formatter={(value, name) => [value, name]}
                              />
                              <Legend
                                verticalAlign="top"
                                align="right"
                                wrapperStyle={{ paddingBottom: '20px' }}
                              />
                              <Area
                                type="monotone"
                                dataKey="Present"
                                stackId="1"
                                stroke="#1B5E20"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorPresentByOp)"
                              />
                              <Area
                                type="monotone"
                                dataKey="Absent"
                                stackId="1"
                                stroke="#B71C1C"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorAbsentByOp)"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AttendanceReport;
